import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { EventDocumentType } from '@application/enums/event-document-type';
import { EventStatusEnum } from '@application/enums/event-status-enum';
import { EventTypeEnum } from '@application/enums/event-type-enum';
import { FieldType } from '@application/enums/field-types';
import { getAllUserCompaniesAsync } from '@application/features/company/companyThunk';
import { createFormFieldAsync, updateFormFieldAsync } from '@application/features/formField/formFieldThunk';
import { getAllUsersByCompany } from '@application/features/user/userThunk';
import { resetVDKEvent } from '@application/features/vdkEvent/VDKEventSlice';
import { createVDKEventAsync, getVDKEventByIdAsync, updateVDKEventAsync } from '@application/features/vdkEvent/VDKEventThunk';
import { DEFAULT_BACKGROUND_IMAGE_URLS, assetToFile, prepareBackgroundImagesForUpload } from '@application/helpers/background-image-helper';
import { useAppDispatch } from '@application/store/useStore';
import { DEFAULT_WELCOME_TEMPLATE, fixBrokenParagraphTags } from '@application/utilities/welcome-text-placeholders';
import { CheckmarkFilled } from '@fluentui/react-icons';
import { ArrowBack, ArrowForward } from '@mui/icons-material';
import TabContext from '@mui/lab/TabContext';
import TabPanel from '@mui/lab/TabPanel';
import { Box, Button, CardContent, Drawer, Tab, Tabs, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import Grid from '@mui/material/Grid2';
import FloatingActionButton from '@ui/components/FloatingButton';
import { PageLayout } from '@ui/components/PageLayout';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { hasPermission } from '@application/helpers/permission-helper';
import { v4 as uuidv4 } from 'uuid';
import isEqual from 'lodash/isEqual';
import cloneDeep from 'lodash/cloneDeep';
import { DynamicForm, EventMetaForm } from './components';
import RightPanel from './RightPanel';
import EventPreview from './RightPanel/EventPreview';
import StartScreenContent from './RightPanel/StartScreenContent';
import useRightPanelStyles from './RightPanel/useRightPanelStyles';
import { SuccessPage } from './SuccessPage';
import useCreateEventStyles from './useCreateEventStyles';
import { getAllCollaboratorsByCompanyAsync } from '@application/features/collaborator/collaboratorThunk';
import { getAllInterestsWithoutPaginationAsync } from '@application/features/interests/interestsThunk';
import { getTranslatedLanguageLabel } from '@application/helpers/language-display-helper';
import { getAllLanguagesAsync } from '@application/features/languages/languagesThunk';
import { filterLanguagesByEventType } from '@application/helpers/language-filter-helper';
import { getPickupLocationsNotPaginatedByEventIdAsync } from '@application/features/pickupLocation/pickupLocationThunk';
import { MagicStar } from 'iconsax-react';
import CustomDialog from '@ui/components/common/Dialog';
import BDrawer from '@ui/components/common/BDrawer';
import { useHelpTopic } from '@ui/contexts';
import UndertakingField from './components/DynamicForm/components/UndertakingField';
import { Icon } from '@iconify/react';
// email template utilities used when creating an event
import { createDefaultEmailTemplatesAsync } from '@application/features/emailTemplates/emailTemplatesThunk';
import { EmailTemplateType } from '@application/features/emailTemplates/models/email-template-type';
// Dutch templates
import { DEFAULT_NEW_EVENT_EMAIL_BODY_DUTCH } from '@ui/pages/Events/EventsHomePage/EventDetails/PreferencesTab/emailConfiguration/templates/dutch/invitationEmailDefaultDutch';
import { DEFAULT_NEW_EVENT_TICKET_EMAIL_BODY_DUTCH } from '@ui/pages/Events/EventsHomePage/EventDetails/PreferencesTab/emailConfiguration/templates/dutch/ticketConfirmationEmailDutch';
import { DEFAULT_NEW_EVENT_THANK_YOU_EMAIL_BODY_DUTCH } from '@ui/pages/Events/EventsHomePage/EventDetails/PreferencesTab/emailConfiguration/templates/dutch/thankYouEmailDefaultDutch';
// English templates
import { DEFAULT_NEW_EVENT_EMAIL_BODY } from '@ui/pages/Events/EventsHomePage/EventDetails/PreferencesTab/emailConfiguration/templates/english/invitationEmailDefaultEng';
import { DEFAULT_NEW_EVENT_THANK_YOU_EMAIL_BODY } from '@ui/pages/Events/EventsHomePage/EventDetails/PreferencesTab/emailConfiguration/templates/english/thankYouEmailDefaultEng';
import { DEFAULT_NEW_EVENT_REINVITATION_REMINDER_EMAIL_BODY } from '@ui/pages/Events/EventsHomePage/EventDetails/PreferencesTab/emailConfiguration/templates/english/reinvitationreminderEmailDefaultEng';
import { DEFAULT_NEW_EVENT_REINVITATION_REMINDER_EMAIL_BODY_DUTCH } from '@ui/pages/Events/EventsHomePage/EventDetails/PreferencesTab/emailConfiguration/templates/dutch/reinvitationreminderEmailDefaultDutch';
import { DEFAULT_NEW_EVENT_REGISTERED_REMINDER_EMAIL_BODY } from '@ui/pages/Events/EventsHomePage/EventDetails/PreferencesTab/emailConfiguration/templates/english/registeredreminderEmailDefaultEng';
import { DEFAULT_NEW_EVENT_REGISTERED_REMINDER_EMAIL_BODY_DUTCH } from '@ui/pages/Events/EventsHomePage/EventDetails/PreferencesTab/emailConfiguration/templates/dutch/registeredreminderEmailDefaultDutch';
import { DEFAULT_NEW_EVENT_TICKET_EMAIL_BODY } from '@ui/pages/Events/EventsHomePage/EventDetails/PreferencesTab/emailConfiguration/templates/english/ticketConfirmationEmailDefaultEng';
// German templates
import { DEFAULT_NEW_EVENT_EMAIL_BODY_GERMAN } from '@ui/pages/Events/EventsHomePage/EventDetails/PreferencesTab/emailConfiguration/templates/german/invitationEmailDefaultGerman';
import { DEFAULT_NEW_EVENT_TICKET_EMAIL_BODY_GERMAN } from '@ui/pages/Events/EventsHomePage/EventDetails/PreferencesTab/emailConfiguration/templates/german/ticketConfirmationEmailDefaultGerman';
import { DEFAULT_NEW_EVENT_THANK_YOU_EMAIL_BODY_GERMAN } from '@ui/pages/Events/EventsHomePage/EventDetails/PreferencesTab/emailConfiguration/templates/german/thankYouEmailDefautGerman';
import { DEFAULT_NEW_EVENT_REINVITATION_REMINDER_EMAIL_BODY_GERMAN } from '@ui/pages/Events/EventsHomePage/EventDetails/PreferencesTab/emailConfiguration/templates/german/reinvitationreminderEmailDefaultGerman';
import { DEFAULT_NEW_EVENT_REGISTERED_REMINDER_EMAIL_BODY_GERMAN } from '@ui/pages/Events/EventsHomePage/EventDetails/PreferencesTab/emailConfiguration/templates/german/registeredreminderEmailDefaultGerman';
// Final Details Email Templates
import { DEFAULT_NEW_EVENT_FINAL_DETAILS_EMAIL_BODY } from '@ui/pages/Events/EventsHomePage/EventDetails/PreferencesTab/emailConfiguration/templates/english/finaldetailEmailDefaultEng';
import { DEFAULT_NEW_EVENT_FINAL_DETAILS_EMAIL_BODY_DUTCH } from '@ui/pages/Events/EventsHomePage/EventDetails/PreferencesTab/emailConfiguration/templates/dutch/finaldetailEmailDefaultDutch';
import { DEFAULT_NEW_EVENT_FINAL_DETAILS_EMAIL_BODY_GERMAN } from '@ui/pages/Events/EventsHomePage/EventDetails/PreferencesTab/emailConfiguration/templates/german/finaldetailEmailDefaultGerman';
// Post-Event Thank You Email Templates
import { DEFAULT_NEW_EVENT_POST_EVENT_THANK_YOU_EMAIL_BODY } from '@ui/pages/Events/EventsHomePage/EventDetails/PreferencesTab/emailConfiguration/templates/english/postEventThankYouEmailDefaultEng';
import { DEFAULT_NEW_EVENT_POST_EVENT_THANK_YOU_EMAIL_BODY_DUTCH } from '@ui/pages/Events/EventsHomePage/EventDetails/PreferencesTab/emailConfiguration/templates/dutch/postEventThankYouEmailDefaultDutch';
import { DEFAULT_NEW_EVENT_POST_EVENT_THANK_YOU_EMAIL_BODY_GERMAN } from '@ui/pages/Events/EventsHomePage/EventDetails/PreferencesTab/emailConfiguration/templates/german/postEventThankYouEmailDefaultGerman';
const formatPickupTimeLabel = (timeOfPickup) => {
    if (!timeOfPickup)
        return '';
    const value = String(timeOfPickup).trim();
    const match = /T(\d{2}):(\d{2})/.exec(value) ?? /^(\d{1,2}):(\d{2})/.exec(value);
    if (!match)
        return '';
    return `${match[1].padStart(2, '0')}:${match[2]}`;
};
// Helper function to convert numeric field type to string
const getFieldTypeString = (fieldType) => {
    const fieldTypeMap = {
        [FieldType.TEXT]: 'text',
        [FieldType.EMAIL]: 'email',
        [FieldType.PHONE]: 'phone',
        [FieldType.DATE]: 'date',
        [FieldType.TIME]: 'time',
        [FieldType.MULTILINE]: 'multiline',
        [FieldType.RADIO]: 'radio',
        [FieldType.CHOICE]: 'choice',
        [FieldType.CHECKBOX_GROUP]: 'checkbox-group',
        [FieldType.DROPDOWN]: 'dropdown',
        [FieldType.COMPANYDROPDOWN]: 'companyDropdown',
        [FieldType.MULTI_SELECT]: 'multi-select',
        [FieldType.TOGGLE]: 'toggle',
        [FieldType.FILE]: 'file',
        [FieldType.IMAGE]: 'image',
        [FieldType.RATING]: 'rating',
        [FieldType.RANKING]: 'ranking',
        [FieldType.NPS]: 'nps',
        [FieldType.UNDERTAKING]: 'undertaking',
        [FieldType.PICKUPLOCATION]: 'pickupLocation',
        [parseInt(FieldType.ADDITIONAL_ATTENDEES)]: 'additionalAttendees'
    };
    return fieldTypeMap[fieldType] || 'text';
};
// Helper function to convert string field type to number for API
const getFieldTypeNumber = (fieldType) => {
    const fieldTypeMap = {
        text: parseInt(FieldType.TEXT),
        email: parseInt(FieldType.EMAIL),
        phone: parseInt(FieldType.PHONE),
        date: parseInt(FieldType.DATE),
        time: parseInt(FieldType.TIME),
        multiline: parseInt(FieldType.MULTILINE),
        radio: parseInt(FieldType.RADIO),
        choice: parseInt(FieldType.CHOICE),
        'checkbox-group': parseInt(FieldType.CHECKBOX_GROUP),
        dropdown: parseInt(FieldType.DROPDOWN),
        companyDropdown: parseInt(FieldType.COMPANYDROPDOWN),
        'single-select-dropdown': parseInt(FieldType.DROPDOWN),
        'multi-select-dropdown': parseInt(FieldType.MULTI_SELECT),
        'multi-select': parseInt(FieldType.MULTI_SELECT),
        toggle: parseInt(FieldType.TOGGLE),
        file: parseInt(FieldType.FILE),
        image: parseInt(FieldType.IMAGE),
        rating: parseInt(FieldType.RATING),
        ranking: parseInt(FieldType.RANKING),
        nps: parseInt(FieldType.NPS),
        undertaking: parseInt(FieldType.UNDERTAKING),
        pickupLocation: parseInt(FieldType.PICKUPLOCATION),
        additionalAttendees: parseInt(FieldType.ADDITIONAL_ATTENDEES),
        'button-group': parseInt(FieldType.RADIO) // Map to radio
    };
    return fieldTypeMap[fieldType] || parseInt(FieldType.TEXT);
};
// Helper function to build default email templates with specific body sections
const buildDefaultEmailTemplates = (languages) => {
    const templates = [];
    const templateConfigs = [];
    // Map language IDs to body content
    languages?.forEach((langObj) => {
        let invitationBody = DEFAULT_NEW_EVENT_EMAIL_BODY;
        let ticketBody = DEFAULT_NEW_EVENT_TICKET_EMAIL_BODY;
        let thankYouBody = DEFAULT_NEW_EVENT_THANK_YOU_EMAIL_BODY;
        let reInvitationReminderBody = DEFAULT_NEW_EVENT_REINVITATION_REMINDER_EMAIL_BODY;
        let registeredReminderBody = DEFAULT_NEW_EVENT_REGISTERED_REMINDER_EMAIL_BODY;
        let finalDetailsBody = DEFAULT_NEW_EVENT_FINAL_DETAILS_EMAIL_BODY;
        let postEventThankYouBody = DEFAULT_NEW_EVENT_POST_EVENT_THANK_YOU_EMAIL_BODY;
        let invitationTemplateName = `Invitation Email`;
        let welcomeTemplateName = `Thank You Email`;
        let ticketTemplateName = `Ticket Confirmation Email`;
        let reInvitationReminderTemplateName = `Re-Invitation Reminder Email`;
        let registeredReminderTemplateName = `Registered Reminder Email`;
        let finalDetailsTemplateName = `Final Details Email`;
        let postEventThankYouTemplateName = `Post-Event Thank You Email`;
        // Dutch templates
        if (langObj?.code?.toLowerCase() === 'nl' || langObj?.name?.toLowerCase().includes('dutch')) {
            invitationBody = DEFAULT_NEW_EVENT_EMAIL_BODY_DUTCH;
            ticketBody = DEFAULT_NEW_EVENT_TICKET_EMAIL_BODY_DUTCH;
            thankYouBody = DEFAULT_NEW_EVENT_THANK_YOU_EMAIL_BODY_DUTCH;
            reInvitationReminderBody = DEFAULT_NEW_EVENT_REINVITATION_REMINDER_EMAIL_BODY_DUTCH;
            registeredReminderBody = DEFAULT_NEW_EVENT_REGISTERED_REMINDER_EMAIL_BODY_DUTCH;
            finalDetailsBody = DEFAULT_NEW_EVENT_FINAL_DETAILS_EMAIL_BODY_DUTCH;
            postEventThankYouBody = DEFAULT_NEW_EVENT_POST_EVENT_THANK_YOU_EMAIL_BODY_DUTCH;
            invitationTemplateName = `Uitnodigingsmail`;
            welcomeTemplateName = `Bedankmail`;
            ticketTemplateName = `Ticketbevestigingsmail`;
            reInvitationReminderTemplateName = `Herinnering opnieuw uitnodigen`;
            registeredReminderTemplateName = `Geregistreerde herinnering`;
            finalDetailsTemplateName = `E-mail met definitieve details`;
            postEventThankYouTemplateName = `Bedankt voor uw bezoek-e-mail`;
        }
        // German templates
        else if (langObj?.code?.toLowerCase() === 'de' ||
            langObj?.name?.toLowerCase().includes('german')) {
            invitationBody = DEFAULT_NEW_EVENT_EMAIL_BODY_GERMAN;
            ticketBody = DEFAULT_NEW_EVENT_TICKET_EMAIL_BODY_GERMAN;
            thankYouBody = DEFAULT_NEW_EVENT_THANK_YOU_EMAIL_BODY_GERMAN;
            reInvitationReminderBody = DEFAULT_NEW_EVENT_REINVITATION_REMINDER_EMAIL_BODY_GERMAN;
            registeredReminderBody = DEFAULT_NEW_EVENT_REGISTERED_REMINDER_EMAIL_BODY_GERMAN;
            finalDetailsBody = DEFAULT_NEW_EVENT_FINAL_DETAILS_EMAIL_BODY_GERMAN;
            postEventThankYouBody = DEFAULT_NEW_EVENT_POST_EVENT_THANK_YOU_EMAIL_BODY_GERMAN;
            invitationTemplateName = `Einladungs-E-Mail`;
            welcomeTemplateName = `Dankes-E-Mail`;
            ticketTemplateName = `Ticketbestätigungs-E-Mail`;
            reInvitationReminderTemplateName = `Erneute Einladungserinnerung`;
            registeredReminderTemplateName = `Registrierungserinnerung`;
            finalDetailsTemplateName = `Abschließende Detaillierungs-E-Mail`;
            postEventThankYouTemplateName = `Dankesmail nach der Veranstaltung`;
        }
        // Default to English
        templateConfigs.push({
            languageId: langObj?.id,
            invitationBody,
            ticketBody,
            thankYouBody,
            reInvitationReminderBody,
            registeredReminderBody,
            finalDetailsBody,
            postEventThankYouBody,
            invitationTemplateName,
            welcomeTemplateName,
            ticketTemplateName,
            reInvitationReminderTemplateName,
            registeredReminderTemplateName,
            finalDetailsTemplateName,
            postEventThankYouTemplateName
        });
    });
    // Create email templates for each language and template type
    templateConfigs.forEach((config) => {
        // Invitation template
        templates.push({
            languageId: config.languageId,
            type: EmailTemplateType.Invitation,
            isActive: true,
            showHeaderImage: true,
            showFeatureImage: true,
            name: config.invitationTemplateName,
            subjectTemplate: 'You are invited to {{sys:eventName}}',
            bodyHtmlTemplate: config.invitationBody,
            status: 1
        });
        // Ticket confirmation template
        templates.push({
            languageId: config.languageId,
            type: EmailTemplateType.Ticket,
            isActive: true,
            showHeaderImage: true,
            showFeatureImage: false,
            name: config.ticketTemplateName,
            subjectTemplate: 'Your ticket for {{sys:eventName}} is ready',
            bodyHtmlTemplate: config.ticketBody,
            status: 1
        });
        // Thank you template
        templates.push({
            languageId: config.languageId,
            type: EmailTemplateType.WelcomeThankYou,
            isActive: true,
            showHeaderImage: true,
            showFeatureImage: true,
            name: config.welcomeTemplateName,
            subjectTemplate: 'Thank you for attending {{field:sys:eventName}}',
            bodyHtmlTemplate: config.thankYouBody,
            status: 1
        });
        // Re-invitation reminder template
        templates.push({
            languageId: config.languageId,
            type: EmailTemplateType.ReInvitationReminder,
            isActive: true,
            showHeaderImage: true,
            showFeatureImage: true,
            name: config.reInvitationReminderTemplateName,
            subjectTemplate: 'You are re invited to {{sys:eventName}}',
            bodyHtmlTemplate: config.reInvitationReminderBody,
            status: 1
        });
        // Registered reminder template
        templates.push({
            languageId: config.languageId,
            type: EmailTemplateType.RegisteredReminder,
            isActive: true,
            showHeaderImage: true,
            showFeatureImage: true,
            name: config.registeredReminderTemplateName,
            subjectTemplate: 'Reminder: {{sys:eventName}} is coming up in 2 days!',
            bodyHtmlTemplate: config.registeredReminderBody,
            status: 1
        });
        // Final Details template
        templates.push({
            languageId: config.languageId,
            type: EmailTemplateType.FinalDetails,
            isActive: true,
            showHeaderImage: true,
            showFeatureImage: true,
            name: config.finalDetailsTemplateName,
            subjectTemplate: 'Final details for {{sys:eventName}}',
            bodyHtmlTemplate: config.finalDetailsBody,
            status: 1
        });
        // // Post-Event Thank You template
        templates.push({
            languageId: config.languageId,
            type: EmailTemplateType.PostEventThankYou,
            isActive: true,
            showHeaderImage: true,
            showFeatureImage: true,
            name: config.postEventThankYouTemplateName,
            subjectTemplate: 'Thank you for attending {{sys:eventName}}',
            bodyHtmlTemplate: config.postEventThankYouBody,
            status: 1
        });
    });
    return templates;
};
export const CreateEvent = () => {
    const theme = useTheme();
    const { i18n, t } = useTranslation();
    const navigate = useNavigate();
    const [tabValue, setTabValue] = useState('form');
    const [isLoading, setIsLoading] = useState(false);
    const searchParams = new URLSearchParams(window.location.search);
    const eventId = searchParams.get('eventId') || undefined;
    const stepParam = searchParams.get('step');
    const languageIdParam = searchParams.get('languageId') || undefined;
    const dispatch = useAppDispatch();
    // Permission checks
    const loggedInUserPermissions = useSelector((state) => state.auth.currentUser?.permissions || []);
    const canCreateEvents = hasPermission(loggedInUserPermissions, 'Permissions.VdkEvents.Create');
    const canUpdateEvents = hasPermission(loggedInUserPermissions, 'Permissions.VdkEvents.Update');
    const cardstyles = useRightPanelStyles();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('xl'));
    const [drawerOpen, setDrawerOpen] = useState(null);
    const [backgroundImages, setBackgroundImages] = useState([]);
    const [undertakingDialogOpen, setUndertakingDialogOpen] = useState(false);
    const [missingUndertakingDialogOpen, setMissingUndertakingDialogOpen] = useState(false);
    const [missingUndertakingLanguages, setMissingUndertakingLanguages] = useState([]);
    const [missingUndertakingLanguageIds, setMissingUndertakingLanguageIds] = useState([]);
    const [missingLanguageFiles, setMissingLanguageFiles] = useState({});
    const [undertakingValidationErrors, setUndertakingValidationErrors] = useState({});
    const [originalLanguageIds, setOriginalLanguageIds] = useState([]);
    const [mode, setMode] = useState('meta');
    useHelpTopic(mode === 'meta' ? 'createEventMeta' : 'createEventSteps');
    const [lockToMetaForPreview, setLockToMetaForPreview] = useState(Boolean(languageIdParam));
    const [hasInitializedStepsLanguage, setHasInitializedStepsLanguage] = useState(false);
    const [activeStep, setActiveStep] = useState(() => {
        // Initialize activeStep from URL parameter if available
        if (stepParam) {
            const stepNumber = parseInt(stepParam);
            return isNaN(stepNumber) ? 0 : Math.max(0, stepNumber - 1); // Convert to 0-based index
        }
        return 0;
    });
    const [showSuccessScreen, setShowSuccessScreen] = useState(false);
    const [isEventDataLoaded, setIsEventDataLoaded] = useState(false);
    const [isLoadingEventData, setIsLoadingEventData] = useState(false);
    const formContainerRef = useRef(null);
    const hasFocusedFirstFieldRef = useRef(false);
    const [eventHasRegistration, setEventHasRegistration] = useState(false);
    const [registeredCompanyIds, setRegisteredCompanyIds] = useState([]);
    const lastFetchedEventIdRef = useRef(undefined);
    const isSubmittingRef = useRef(false);
    // Snapshot of `steps` taken right before a form-step removal; restored if the
    // subsequent submit fails (e.g. backend rejects deleting a step with existing responses)
    const lastRemovedStepsSnapshotRef = useRef(null);
    const [extraInformationRows, setExtraInformationRows] = useState([]);
    // Handle language selection coming from Translations tab (or right panel changes)
    const [selectedLanguageId, setSelectedLanguageId] = useState(languageIdParam);
    const [showDiscardChangesPopup, setShowDiscardChangesPopup] = useState(false);
    const [duplicateVdkEvent, setDuplicateVdkEvent] = useState(undefined);
    const isTranslationPreview = Boolean(languageIdParam);
    const entryMode = useMemo(() => {
        if (isTranslationPreview)
            return 'translationPreview';
        return eventId ? 'edit' : 'create';
    }, [isTranslationPreview, eventId]);
    // Apply a fetched event to the form state (shared between initial load and language changes)
    const applyEventToForm = (event) => {
        // Set default selected language to primary (or first) if not set
        if (!selectedLanguageId &&
            Array.isArray(event.eventLanguages) &&
            event.eventLanguages.length > 0) {
            const primary = event.eventLanguages.find((l) => l.isPrimary);
            const fallback = event.eventLanguages[0];
            const defaultLangId = (primary?.id || fallback?.id);
            if (defaultLangId) {
                setSelectedLanguageId(defaultLangId);
            }
        }
        // Track if event already has registrations
        setEventHasRegistration(Boolean(event.hasRegistration));
        setRegisteredCompanyIds(Array.isArray(event.registeredCompanyIds) ? event.registeredCompanyIds : []);
        setValue('eventTitle', event.title);
        setValue('location', event.location ?? '');
        // Convert assigned users to the format expected by the multi-select component
        const assignedUsersList = Array.isArray(event.assignedUsers) ? event.assignedUsers : [];
        const assignedUserObjects = assignedUsersList.map((user) => ({
            label: `${user.firstName} ${user.lastName}`,
            value: user.id
        }));
        setValue('assigneeIds', assignedUserObjects);
        // Prefill collaborators like assignees, using assignedCollaborators when available
        const assignedCollabList = Array.isArray(event.assignedCollaborators)
            ? event.assignedCollaborators
            : [];
        const collaboratorObjects = assignedCollabList.length > 0
            ? assignedCollabList.map((c) => ({
                label: `${c.firstName} ${c.lastName}`.trim(),
                value: c.id
            }))
            : (Array.isArray(event.collaboratorIds) ? event.collaboratorIds : []).map((cc) => {
                const id = typeof cc === 'object' && 'id' in cc ? cc.id : cc;
                const match = collaborators?.find((c) => c.id === id);
                return {
                    label: match ? `${match.firstName} ${match.lastName}`.trim() : id,
                    value: id
                };
            });
        setValue('collaboratorIds', collaboratorObjects);
        // Prefill Languages similar to Interests
        const eventLanguagesList = Array.isArray(event.eventLanguages) ? event.eventLanguages : [];
        const assignedLanguageObjects = eventLanguagesList.map((l) => ({
            label: getTranslatedLanguageLabel(t, l) || l.code,
            value: l.id,
            iconUrl: l.flagUrl
        }));
        // Map Extra Informations from backend
        if (Array.isArray(event.extraInformations)) {
            const mappedExtraInformations = event.extraInformations.map((info) => ({
                id: info.id,
                title: info.title,
                description: info.description,
                fileurl: info.documentUrl,
                documentname: info.documentName,
                file: null // backend does not send File objects
            }));
            setExtraInformationRows(mappedExtraInformations);
        }
        else {
            setExtraInformationRows([]);
        }
        // Preserve sidebar language if it was added before event loaded
        const currentLanguageIds = getValues('languageIds') || [];
        const eventLangIdStrings = assignedLanguageObjects.map((l) => String(l.value));
        // Find languages in current form that are not in event languages (these might be sidebar-added)
        const sidebarAddedLanguages = currentLanguageIds.filter((l) => {
            const val = typeof l === 'object' && 'value' in l ? l.value : l;
            return !eventLangIdStrings.includes(String(val));
        });
        // Add sidebar-added languages to the assigned languages (preserve them)
        if (sidebarAddedLanguages.length > 0) {
            sidebarAddedLanguages.forEach((sidebarLang) => {
                const sidebarLangValue = typeof sidebarLang === 'object' && 'value' in sidebarLang
                    ? sidebarLang.value
                    : sidebarLang;
                if (!assignedLanguageObjects.some((l) => String(l.value) === String(sidebarLangValue))) {
                    assignedLanguageObjects.push(sidebarLang);
                }
            });
        }
        setValue('languageIds', assignedLanguageObjects);
        const eventLangIds = eventLanguagesList.map((l) => l.id);
        setOriginalLanguageIds(eventLangIds);
        // Set primary language ID from event
        const primaryLang = eventLanguagesList.find((l) => l.isPrimary);
        if (primaryLang?.id) {
            setValue('primaryLanguageId', primaryLang.id);
        }
        // Prefill Interests like collaborator, assignees (API may return single object or array)
        const eventInterests = Array.isArray(event.eventInterest)
            ? event.eventInterest
            : event.eventInterest
                ? [event.eventInterest]
                : [];
        const assignedInterestObject = eventInterests.map((interest) => ({
            label: `${interest.title}`,
            value: interest.id
        }));
        setValue('interests', assignedInterestObject);
        setValue('externalsAllowed', event.isExternalAllowed);
        setValue('noOfSteps', event.noOfSteps.toString());
        setValue('welcomeText', fixBrokenParagraphTags(event.welcomeText ?? ''));
        setValue('thankYouText', fixBrokenParagraphTags(event.thankYouText ?? ''));
        setValue('contactEventTeamPhone', event.contactEventTeamPhone ?? '');
        setValue('contactEventTeamEmail', event.contactEventTeamEmail ?? '');
        setValue('details', event.details);
        setValue('floorPlan', event.floorPlan);
        setValue('passwordProtection', event.isPasswordProtection);
        setValue('password', event.password || '');
        const resolvedIsMultiDayEvent = typeof event.isMultiDayEvent === 'boolean'
            ? event.isMultiDayEvent
            : Boolean(event.startDate && event.endDate && event.startDate !== event.endDate);
        setValue('isMultiDayEvent', resolvedIsMultiDayEvent);
        setValue('eventDuration', {
            startDate: event.startDate,
            endDate: event.endDate
        });
        setValue('startTime', event.startTime || null);
        setValue('endTime', event.endTime || null);
        setValue('companies', event.eventCompanies);
        const rawMergedPickupLocations = (pickupLocationsList && pickupLocationsList.length > 0
            ? pickupLocationsList
            : event.pickupLocations || []);
        const mergedPickupLocations = rawMergedPickupLocations.filter((loc) => !loc.isClosed);
        setValue('pickupLocations', mergedPickupLocations);
        // Map form steps with proper options handling
        const formSteps = Array.isArray(event.formSteps) ? event.formSteps : [];
        const mappedFormSteps = formSteps.map((step) => {
            const stepFormFields = Array.isArray(step.formFields) ? step.formFields : [];
            // Backend may return RelatedFormFieldId (PascalCase) or relatedFormFieldId (camelCase)
            const referredByFields = stepFormFields.filter((field) => field.relatedFormFieldId || field.RelatedFormFieldId);
            const regularFields = stepFormFields.filter((field) => !field.relatedFormFieldId && !field.RelatedFormFieldId);
            const referredByFieldsByParent = {};
            referredByFields.forEach((field) => {
                const parentId = field.relatedFormFieldId || field.RelatedFormFieldId;
                if (!parentId)
                    return;
                if (!referredByFieldsByParent[parentId]) {
                    referredByFieldsByParent[parentId] = [];
                }
                referredByFieldsByParent[parentId].push(field);
            });
            // Map regular fields and attach referredBy fields to companyDropdown fields
            const mappedFields = regularFields
                .map((field) => {
                // Extract options from fieldOptions or options
                const mappedOptions = Array.isArray(field.fieldOptions)
                    ? field.fieldOptions.map((option) => ({
                        id: option.id || option.optionId,
                        label: option.label || option.text || option.name,
                        value: typeof option.value === 'number' ? option.value : parseInt(option.value) || 0,
                        isFromBackend: true
                    }))
                    : Array.isArray(field.options)
                        ? field.options.map((option) => ({
                            id: option.id || option.optionId,
                            label: option.label || option.text || option.name,
                            value: typeof option.value === 'number' ? option.value : parseInt(option.value) || 0,
                            isFromBackend: true
                        }))
                        : [];
                // Convert field type properly
                let convertedFieldType = typeof field.fieldType === 'number'
                    ? getFieldTypeString(field.fieldType)
                    : typeof field.fieldType === 'string' && !isNaN(parseInt(field.fieldType))
                        ? getFieldTypeString(parseInt(field.fieldType))
                        : field.fieldType || field.fieldTypeId;
                const fieldAdditionalNote = field.fieldAdditionalNote ?? field.FieldAdditionalNote ?? '';
                const pickupLocationOptions = convertedFieldType === 'pickupLocation'
                    ? mergedPickupLocations.map((loc, idx) => ({
                        id: loc.id,
                        label: loc.name,
                        value: idx + 1,
                        isFromBackend: true,
                        isClosed: loc.isClosed ?? false
                    }))
                    : [];
                // Find uploaded undertaking files by language
                let uploadedFileData = undefined;
                let undertakingUploadedFilesByLanguage = undefined;
                if (convertedFieldType === 'undertaking' &&
                    event.undertakingFiles &&
                    Array.isArray(event.undertakingFiles)) {
                    const pdfs = event.undertakingFiles.filter((img) => !img.isBackgroundImage && (img.name?.toLowerCase()?.includes('.pdf') || true));
                    if (pdfs.length > 0) {
                        // Group by languageId
                        const byLang = {};
                        pdfs.forEach((f) => {
                            const langId = String(f.languageId || '');
                            if (!langId)
                                return;
                            const mapped = {
                                id: f.id,
                                url: f.url,
                                name: f.name,
                                fileSize: '0',
                                folderName: 'Undertaking',
                                type: f.type,
                                eventId: f.eventId,
                                isBackgroundImage: f.isBackgroundImage,
                                isTemporary: false
                            };
                            byLang[langId] = [...(byLang[langId] || []), mapped];
                        });
                        undertakingUploadedFilesByLanguage = byLang;
                        // Keep first file as legacy single for compatibility/locking
                        const first = pdfs[0];
                        uploadedFileData = first
                            ? {
                                id: first.id,
                                url: first.url,
                                name: first.name,
                                fileSize: '0',
                                folderName: 'Undertaking',
                                type: first.type,
                                eventId: first.eventId,
                                isBackgroundImage: first.isBackgroundImage,
                                isTemporary: false
                            }
                            : undefined;
                    }
                }
                // Map referredBy fields to companyDropdownFields structure for companyDropdown fields
                let companyDropdownFields = undefined;
                if (convertedFieldType === 'companyDropdown' && referredByFieldsByParent[field.id]) {
                    const referredByFieldsForThisField = referredByFieldsByParent[field.id];
                    const sortedFields = [...referredByFieldsForThisField].sort((a, b) => (a.fieldOrder || 0) - (b.fieldOrder || 0));
                    // Map referredBy fields to companyDropdownFields structure
                    const fullNameField = sortedFields[0];
                    const emailField = sortedFields[1];
                    const companyNameField = sortedFields[2];
                    companyDropdownFields = [
                        {
                            id: 'fullName',
                            backendId: fullNameField?.id,
                            label: fullNameField?.label,
                            enabled: true,
                            required: true,
                            labelTranslations: fullNameField?.labelTranslations || []
                        },
                        {
                            id: 'email',
                            backendId: emailField?.id,
                            label: emailField?.label,
                            enabled: emailField
                                ? emailField.isEnabled === true || emailField.isEnabled === undefined
                                : false,
                            required: emailField ? emailField?.isRequired : false,
                            labelTranslations: emailField?.labelTranslations || []
                        },
                        {
                            id: 'companyName',
                            backendId: companyNameField?.id,
                            label: companyNameField?.label,
                            enabled: true,
                            required: true,
                            labelTranslations: companyNameField?.labelTranslations || []
                        }
                    ];
                }
                // Additional Attendees: sub-questions are real sibling fields (RelatedFormFieldId = this
                // field's id) - reconstruct the editor's nested `additionalAttendeeFields` array from the
                // same referredByFieldsByParent grouping used for Company Dropdown above.
                let additionalAttendeeFields = undefined;
                if (convertedFieldType === 'additionalAttendees' && referredByFieldsByParent[field.id]) {
                    additionalAttendeeFields = [...referredByFieldsByParent[field.id]]
                        .sort((a, b) => (a.fieldOrder || 0) - (b.fieldOrder || 0))
                        .map((subField) => {
                        const subFieldType = typeof subField.fieldType === 'number'
                            ? getFieldTypeString(subField.fieldType)
                            : typeof subField.fieldType === 'string' && !isNaN(parseInt(subField.fieldType))
                                ? getFieldTypeString(parseInt(subField.fieldType))
                                : subField.fieldType;
                        const subFieldOptions = Array.isArray(subField.fieldOptions)
                            ? subField.fieldOptions
                            : Array.isArray(subField.options)
                                ? subField.options
                                : [];
                        return {
                            id: subField.id,
                            label: subField.label,
                            fieldType: subFieldType,
                            fieldParent: subField.fieldParent ?? '',
                            fieldChild: subField.fieldChild,
                            fieldOrder: subField.fieldOrder || 0,
                            required: Boolean(subField.isRequired ?? subField.required),
                            role: subField.subFieldRole ?? subField.SubFieldRole ?? undefined,
                            isFromBackend: true,
                            options: subFieldOptions.map((option) => ({
                                id: option.id || option.optionId,
                                label: option.label || option.text || option.name,
                                value: typeof option.value === 'number' ? option.value : parseInt(option.value) || 0,
                                isFromBackend: true
                            }))
                        };
                    });
                }
                // Return field without fieldOptions, only with options
                return {
                    id: field.id,
                    fieldType: convertedFieldType,
                    label: field.label,
                    required: convertedFieldType === 'toggle' ? false : Boolean(field.isRequired || field.required),
                    isIdentity: field.isIdentity,
                    fieldParent: field.fieldParent,
                    fieldChild: field.fieldChild,
                    fieldOrder: field.fieldOrder || 0,
                    descriptionForToolTip: field.descriptionForToolTip,
                    // Used by option-chaining UI to determine whether a target question can have multiple parent options.
                    fieldAdditionalNote: fieldAdditionalNote,
                    options: convertedFieldType === 'pickupLocation'
                        ? pickupLocationOptions.length > 0
                            ? pickupLocationOptions
                            : mappedOptions
                        : mappedOptions,
                    isFromBackend: true,
                    uploadedFileData: uploadedFileData,
                    undertakingUploadedFilesByLanguage: undertakingUploadedFilesByLanguage,
                    undertakingToggleLabel: field.undertakingToggleLabel,
                    undertakingAlertNote: field.undertakingAlertNote,
                    companyDropdownFields: companyDropdownFields,
                    maxAdditionalAttendees: field.maxAdditionalAttendees ?? field.MaxAdditionalAttendees,
                    maxAdditionalAttendeesReachedMessage: field.maxAdditionalAttendeesReachedMessage ?? field.MaxAdditionalAttendeesReachedMessage,
                    additionalAttendeeFields: additionalAttendeeFields,
                    parentOptionId: field.parentOptionId ?? field.ParentOptionId ?? null,
                    parentOptionIds: field.parentOptionIds ?? field.ParentOptionIds ?? [],
                    parentFieldId: field.parentFieldId ?? field.ParentFieldId ?? null,
                    parentFieldLabel: field.parentFieldLabel ?? field.ParentFieldLabel ?? null,
                    parentOptionValue: field.parentOptionValue ?? field.ParentOptionValue ?? null,
                    isBranchingEnabled: field.isBranchingEnabled ?? field.IsBranchingEnabled ?? undefined,
                    relatedFormFieldId: field.relatedFormFieldId,
                    isEnabled: field.isEnabled,
                    hasResponses: field.hasResponses ?? false
                };
            })
                .sort((a, b) => (a.fieldOrder || 0) - (b.fieldOrder || 0));
            // Rebuild option-level branching selections from saved child.parentOptionId(s) links
            const optionToChildFieldIds = mappedFields.reduce((acc, field) => {
                const parentOptionIds = Array.isArray(field.parentOptionIds)
                    ? field.parentOptionIds
                    : field.parentOptionId
                        ? [field.parentOptionId]
                        : [];
                if (!parentOptionIds.length || !field.id)
                    return acc;
                parentOptionIds.forEach((parentOptionId) => {
                    if (!acc[parentOptionId]) {
                        acc[parentOptionId] = [];
                    }
                    acc[parentOptionId].push(field.id);
                });
                return acc;
            }, {});
            const mappedFieldsWithBranching = mappedFields.map((field) => {
                // Toggle branching uses fixed Yes/No options even when backend returns no options.
                const hasNoOptions = !Array.isArray(field.options) || field.options.length === 0;
                const normalizedOptions = field.fieldType === 'toggle' && hasNoOptions
                    ? [
                        {
                            id: `${field.id}_yes`,
                            label: t('events.toggleOptions.yes') || 'Yes',
                            value: 1,
                            isFromBackend: false
                        },
                        {
                            id: `${field.id}_no`,
                            label: t('events.toggleOptions.no') || 'No',
                            value: 0,
                            isFromBackend: false
                        }
                    ]
                    : field.options;
                if (!Array.isArray(normalizedOptions) || normalizedOptions.length === 0)
                    return field;
                const persistedBranchingEnabled = field.isBranchingEnabled === true;
                const optionsWithBranching = normalizedOptions.map((option) => ({
                    ...option,
                    optionChainingShowField: optionToChildFieldIds[option.id] || (persistedBranchingEnabled ? [] : [])
                }));
                const hasBranchingTargets = optionsWithBranching.some((option) => Array.isArray(option.optionChainingShowField) &&
                    option.optionChainingShowField.length > 0);
                return {
                    ...field,
                    options: optionsWithBranching,
                    isBranchingEnabled: typeof field.isBranchingEnabled === 'boolean'
                        ? field.isBranchingEnabled
                        : hasBranchingTargets
                };
            });
            return {
                ...step,
                formFields: mappedFieldsWithBranching,
                description: step.description ?? ''
            };
        });
        setValue('steps', mappedFormSteps.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)));
        // Set background images from backend
        if (event.uploadedImages && Array.isArray(event.uploadedImages)) {
            const mappedImages = event.uploadedImages.map((img) => {
                const mappedImage = {
                    ...img,
                    url: `${img.url || img.path || img.fileName}`,
                    isBackgroundImage: img.isBackgroundImage || false,
                    isSelected: img.isBackgroundImage || false // Select background image
                };
                return mappedImage;
            });
            setBackgroundImages(mappedImages);
        }
        else {
            setBackgroundImages([]);
        }
        // Mark event data as loaded after setting all values
        setIsEventDataLoaded(true);
        setIsLoadingEventData(false);
        // Trigger validation for all fields after setting values to ensure validation works
        trigger();
        // Store a deep-cloned snapshot of the synced values so we can compare
        // the user's current form values against this baseline later.
        try {
            setDuplicateVdkEvent(cloneDeep(getValues()));
        }
        catch (e) {
            // ignore cloning errors
            setDuplicateVdkEvent(undefined);
        }
        // If no steps exist and URL asked for steps, keep user on meta page
        const hasSteps = Array.isArray(event.formSteps) && event.formSteps.length > 0;
        if (!hasSteps && stepParam) {
            setMode('meta');
            setActiveStep(0);
            const newSearchParams = new URLSearchParams(window.location.search);
            newSearchParams.delete('step');
            navigate(`/create-event?${newSearchParams.toString()}`, { replace: true });
        }
    };
    // Get data from Redux store
    const { companies, companiesByRegion, selectedCompanyId } = useSelector((state) => state.company);
    const { usersByCompany: users } = useSelector((state) => state.user);
    const { currentUser } = useSelector((state) => state.auth);
    const { collaborators } = useSelector((state) => state.collaborator);
    const { interestswithoutpagination } = useSelector((state) => state.interests);
    const { languages } = useSelector((state) => state.languages);
    const vdkEvent = useSelector((state) => state.VDKEvent.VDKEvent);
    const { pickupLocationsList } = useSelector((state) => state.pickupLocation);
    const stepKeys = ['personalInfo', 'plusOneInfo', 'transport', 'accommodation', 'Misc'];
    const defaultStepNames = ['Personal info', 'Plus-one info', 'Transport', 'Accommodation', 'Misc'];
    const defaultStepDescriptions = [
        'This step gathers essential attendee information required for event registration.',
        'Add plus-one details for attendees.',
        'Capture travel or shuttle preferences.',
        'Collect lodging preferences and needs.',
        'Any additional information or requests.'
    ];
    // Check if user has spheremaster/sfeermeester role
    const isSpheremaster = useMemo(() => {
        if (!currentUser?.role)
            return false;
        const roles = Array.isArray(currentUser.role) ? currentUser.role : [currentUser.role];
        return roles.some((role) => role.toLowerCase().includes('spheremaster') ||
            role.toLowerCase().includes('sfeermeester') ||
            role === 'Sfeermeester');
    }, [currentUser]);
    const isCollaborator = Array.isArray(currentUser?.role) &&
        (currentUser?.role.includes('Collaborator') || currentUser?.role.includes('TransportManager'));
    useEffect(() => {
        if (isCollaborator) {
            navigate('/', { replace: true });
        }
    }, [isCollaborator, navigate]);
    // Load companies on component mount
    useEffect(() => {
        dispatch(getAllUserCompaniesAsync());
        dispatch(getAllInterestsWithoutPaginationAsync());
    }, [dispatch]);
    useEffect(() => {
        dispatch(getAllLanguagesAsync());
    }, [dispatch]);
    useEffect(() => {
        if (!eventId)
            return;
        dispatch(getPickupLocationsNotPaginatedByEventIdAsync({
            eventId,
            includeClosed: false
        }));
    }, [dispatch, eventId]);
    // Transform companiesByRegion for grouped dropdown
    const groupedCompanyOptions = (companiesByRegion || []).flatMap((region) => (region.companies || []).map((company) => ({
        label: company.name,
        value: company.id,
        regionKey: region.regionKey,
        regionLabel: region.regionLabel
    })));
    // Transform collaborators for collaboratorIds dropdown
    const collaboratorOptions = useMemo(() => (Array.isArray(collaborators) ? collaborators : []).map((c) => ({
        label: `${c.firstName} ${c.lastName}`.trim(),
        value: c.id
    })), [collaborators]);
    // When in translation preview, get the selected language's code for step label (e.g. 'nl', 'de')
    const translationLanguageCode = useMemo(() => {
        if (!isTranslationPreview || !selectedLanguageId || !vdkEvent?.eventLanguages?.length)
            return undefined;
        const lang = vdkEvent.eventLanguages.find((l) => String(l.id) === String(selectedLanguageId));
        if (!lang)
            return undefined;
        const code = (lang.locale || lang.code || '').split('-')[0]?.toLowerCase();
        return code || undefined;
    }, [isTranslationPreview, selectedLanguageId, vdkEvent?.eventLanguages]);
    const languageOptions = useMemo(() => {
        const isFamilyEvent = vdkEvent?.type === EventTypeEnum.Family;
        if (isTranslationPreview) {
            return (vdkEvent?.eventLanguages || []).map((lang) => ({
                label: getTranslatedLanguageLabel(t, lang),
                value: lang.id,
                iconUrl: lang.flagUrl,
                isDisabled: true
            }));
        }
        // Don't disable any languages in the event languages dropdown
        // Filter languages based on event type - exclude SV for non-FamilyFest events
        const filteredLanguages = filterLanguagesByEventType(languages, isFamilyEvent);
        return (filteredLanguages || []).map((lang) => ({
            label: getTranslatedLanguageLabel(t, lang),
            value: lang.id,
            iconUrl: lang.flagUrl
        }));
    }, [isTranslationPreview, vdkEvent?.eventLanguages, languages, vdkEvent?.type, t, i18n.language]);
    // Transform Interests for InterestDropdown
    const interestOptions = (Array.isArray(interestswithoutpagination) ? interestswithoutpagination : []).map((interest) => ({
        label: interest.title,
        value: interest.id
    }));
    const DefaultValues = {
        companies: [],
        eventTitle: '',
        location: '',
        assigneeIds: [],
        collaboratorIds: [],
        externalsAllowed: true,
        noOfSteps: '5',
        welcomeText: DEFAULT_WELCOME_TEMPLATE,
        thankYouText: '',
        contactEventTeamPhone: '',
        contactEventTeamEmail: '',
        details: '',
        floorPlan: '',
        passwordProtection: false,
        password: '',
        backgroundImage: '',
        isMultiDayEvent: false,
        eventDuration: { startDate: '', endDate: '' },
        startTime: null,
        endTime: null,
        steps: [
            { id: uuidv4(), name: t('eventSteps.personalInfoTitle'), formFields: [] },
            { id: uuidv4(), name: t('eventSteps.plusOneInfoTitle'), formFields: [] },
            { id: uuidv4(), name: t('eventSteps.transportTitle'), formFields: [] },
            { id: uuidv4(), name: t('eventSteps.accommodationTitle'), formFields: [] },
            { id: uuidv4(), name: t('eventSteps.miscTitle'), formFields: [] }
        ],
        interests: [],
        languageIds: [],
        primaryLanguageId: undefined
    };
    const { control, watch, handleSubmit, formState: { errors }, reset, setValue, getValues, clearErrors, trigger, setError } = useForm({
        mode: 'onChange',
        reValidateMode: 'onChange',
        defaultValues: DefaultValues
    });
    const selectedCompanies = watch('companies');
    const watchedSteps = watch('steps');
    useEffect(() => {
        if (!pickupLocationsList)
            return;
        const openPickupLocations = pickupLocationsList.filter((loc) => !loc.isClosed);
        const currentPickupLocations = (getValues('pickupLocations') || []);
        const shouldUpdatePickupLocations = currentPickupLocations.length !== openPickupLocations.length ||
            currentPickupLocations.some((loc, idx) => loc?.id !== openPickupLocations[idx]?.id);
        if (shouldUpdatePickupLocations) {
            setValue('pickupLocations', openPickupLocations, { shouldDirty: false });
        }
        const steps = (getValues('steps') || []);
        if (!Array.isArray(steps) || steps.length === 0)
            return;
        const pickupOptions = openPickupLocations.map((loc, idx) => ({
            id: loc.id,
            label: formatPickupTimeLabel(loc.timeOfPickup)
                ? `${loc.name} (${formatPickupTimeLabel(loc.timeOfPickup)})`
                : loc.name,
            value: idx + 1,
            isFromBackend: true,
            isClosed: loc.isClosed ?? false,
            isAccommodationAvailable: loc.isAccommodationAvailable === true
        }));
        const updatedSteps = steps.map((step) => {
            const isAccommodationField = (field) => field?.fieldAdditionalNote === 'preset.transport.accommodationRequired' ||
                String(field?.label ?? '')
                    .trim()
                    .toLowerCase() === 'accommodation required';
            const updatedFields = (step.formFields || []).map((field) => {
                if (field.fieldType !== 'pickupLocation' &&
                    String(field.fieldType) !== String(FieldType.PICKUPLOCATION)) {
                    return field;
                }
                const accommodationRequiredField = (step.formFields || []).find((f) => isAccommodationField(f));
                const currentOptionsById = new Map((field.options || []).map((opt) => [String(opt.id), opt]));
                return {
                    ...field,
                    options: pickupOptions.map((opt) => ({
                        id: opt.id,
                        label: opt.label,
                        value: opt.value,
                        isFromBackend: opt.isFromBackend,
                        isClosed: opt.isClosed,
                        optionChainingShowField: opt.isAccommodationAvailable && accommodationRequiredField?.id
                            ? [accommodationRequiredField.id]
                            : currentOptionsById.get(String(opt.id))?.optionChainingShowField || []
                    }))
                };
            });
            const pickupField = updatedFields.find((f) => f.fieldType === 'pickupLocation' ||
                String(f.fieldType) === String(FieldType.PICKUPLOCATION));
            const accommodationFieldIndex = updatedFields.findIndex((f) => isAccommodationField(f));
            if (pickupField && accommodationFieldIndex >= 0) {
                const accommodationField = updatedFields[accommodationFieldIndex];
                const parentOptionIds = (pickupField.options || [])
                    .filter((option) => (option.optionChainingShowField || []).includes(accommodationField.id))
                    .map((option) => option.id);
                updatedFields[accommodationFieldIndex] = {
                    ...accommodationField,
                    parentOptionIds,
                    parentOptionId: parentOptionIds[0] ?? null
                };
            }
            return {
                ...step,
                formFields: updatedFields
            };
        });
        const hasPickupField = steps.some((step) => (step.formFields || []).some((field) => field.fieldType === 'pickupLocation' ||
            String(field.fieldType) === String(FieldType.PICKUPLOCATION)));
        if (!hasPickupField)
            return;
        const hasOptionMismatch = steps.some((step, stepIdx) => (step.formFields || []).some((field, fieldIdx) => {
            if (field.fieldType !== 'pickupLocation' &&
                String(field.fieldType) !== String(FieldType.PICKUPLOCATION)) {
                return false;
            }
            const currentOptions = field.options || [];
            const expectedOptions = updatedSteps?.[stepIdx]?.formFields?.[fieldIdx]?.options || [];
            if (currentOptions.length !== expectedOptions.length)
                return true;
            return currentOptions.some((opt, idx) => {
                const expected = expectedOptions[idx] || {};
                const currentShow = Array.isArray(opt?.optionChainingShowField)
                    ? opt.optionChainingShowField
                    : [];
                const expectedShow = Array.isArray(expected?.optionChainingShowField)
                    ? expected.optionChainingShowField
                    : [];
                return (opt?.id !== expected?.id ||
                    opt?.label !== expected?.label ||
                    currentShow.length !== expectedShow.length ||
                    currentShow.some((id, showIdx) => id !== expectedShow[showIdx]));
            });
        }));
        const hasAccommodationLinkMismatch = steps.some((step, stepIdx) => {
            const isAccommodationField = (field) => field?.fieldAdditionalNote === 'preset.transport.accommodationRequired' ||
                String(field?.label ?? '')
                    .trim()
                    .toLowerCase() === 'accommodation required';
            const currentField = (step.formFields || []).find((f) => isAccommodationField(f));
            const expectedField = (updatedSteps?.[stepIdx]?.formFields || []).find((f) => isAccommodationField(f));
            if (!currentField || !expectedField)
                return false;
            const currentIds = Array.isArray(currentField.parentOptionIds)
                ? currentField.parentOptionIds.map(String)
                : [];
            const expectedIds = Array.isArray(expectedField.parentOptionIds)
                ? expectedField.parentOptionIds.map(String)
                : [];
            return (currentIds.length !== expectedIds.length ||
                currentIds.some((id, idx) => id !== expectedIds[idx]));
        });
        if (hasOptionMismatch || hasAccommodationLinkMismatch) {
            setValue('steps', updatedSteps, { shouldDirty: false });
        }
    }, [pickupLocationsList, watchedSteps, getValues, setValue]);
    // Get selected company IDs for filtering (used in userOptions)
    const selectedCompanyIds = useMemo(() => {
        if (!selectedCompanies || selectedCompanies.length === 0)
            return [];
        return selectedCompanies.map((company) => typeof company === 'object' && company.value ? company.value : company);
    }, [selectedCompanies]);
    // Transform users for assigneeIds dropdown
    // If spheremaster is logged in, filter to show only spheremasters
    const userOptions = useMemo(() => {
        const userList = Array.isArray(users) ? users : [];
        if (isSpheremaster) {
            // Filter users by role (only spheremasters)
            const filteredUsers = userList.filter((user) => {
                if (!user.role)
                    return false;
                const role = user.role.toLowerCase();
                return (role.includes('spheremaster') || role.includes('sfeermeester') || role === 'sfeermeester');
            });
            return filteredUsers.map((user) => ({
                label: user.fullName,
                value: user.id
            }));
        }
        // For non-spheremasters, show all users
        return userList.map((user) => ({
            label: user.fullName,
            value: user.id
        }));
    }, [users, isSpheremaster]);
    const primaryLanguageId = watch('primaryLanguageId');
    const isPrimaryLanguage = useMemo(() => {
        // Check if selected language is the primary language from form
        return primaryLanguageId ? String(primaryLanguageId) === String(selectedLanguageId) : false;
    }, [primaryLanguageId, selectedLanguageId]);
    // When navigating from Edit (eventId present) to New Event (no eventId), reset state so the new-event init effect runs
    useEffect(() => {
        if (!eventId) {
            setIsEventDataLoaded(false);
            lastFetchedEventIdRef.current = undefined;
            setHasInitializedStepsLanguage(false);
            setShowSuccessScreen(false);
        }
        else {
            hasFocusedFirstFieldRef.current = false;
        }
    }, [eventId]);
    // Focus first form field when New Event form is shown
    useEffect(() => {
        if (eventId || !isEventDataLoaded || mode !== 'meta' || hasFocusedFirstFieldRef.current)
            return;
        const timer = setTimeout(() => {
            const first = formContainerRef.current?.querySelector('input:not([type=hidden]):not([readonly]), select, textarea');
            first?.focus();
            hasFocusedFirstFieldRef.current = true;
        }, 100);
        return () => clearTimeout(timer);
    }, [eventId, isEventDataLoaded, mode]);
    // Initialize default background images for new events and reset form
    // Only run on initial mount for new events, not on language changes
    useEffect(() => {
        if (!eventId && !isEventDataLoaded) {
            // Reset form for new events with proper default values
            // No default languages - user must explicitly select all languages
            reset(DefaultValues);
            clearErrors();
            setShowSuccessScreen(false);
            setIsEventDataLoaded(true); // Mark as loaded for new events
            // Set default background images for new events
            const defaultImages = DEFAULT_BACKGROUND_IMAGE_URLS.map((url, index) => ({
                url: url,
                isSelected: index === 0,
                isBackgroundImage: index === 0,
                isDefaultImage: true
            }));
            setBackgroundImages(defaultImages);
        }
    }, [eventId, reset, clearErrors, languages, t, isEventDataLoaded]);
    // Keep track of companies and sync with UserHeader for New Events ONLY
    const previousSelectedCompanyIds = useRef([]);
    useEffect(() => {
        if (eventId)
            return; // Do not run for Edit Event
        if (!groupedCompanyOptions || groupedCompanyOptions.length === 0)
            return; // Wait for options to load
        const currentSelected = selectedCompanyId || [];
        const prevSelected = previousSelectedCompanyIds.current;
        // Diffing: find what was added and removed since last run
        const addedIds = currentSelected.filter(id => !prevSelected.includes(id));
        const removedIds = prevSelected.filter(id => !currentSelected.includes(id));
        if (addedIds.length > 0 || removedIds.length > 0) {
            const currentFormCompanies = getValues('companies') || [];
            let newFormCompanies = [...currentFormCompanies];
            // Remove companies that were deselected from Global Header
            if (removedIds.length > 0) {
                newFormCompanies = newFormCompanies.filter((comp) => {
                    const id = typeof comp === 'object' && comp.value ? comp.value : comp;
                    return !removedIds.includes(id);
                });
            }
            // Add companies that were selected in Global Header
            addedIds.forEach(id => {
                const isAlreadyInForm = newFormCompanies.some((comp) => {
                    const compId = typeof comp === 'object' && comp.value ? comp.value : comp;
                    return compId === id;
                });
                if (!isAlreadyInForm) {
                    const companyOption = groupedCompanyOptions.find(opt => opt.value === id);
                    if (companyOption) {
                        newFormCompanies.push(companyOption);
                    }
                }
            });
            setValue('companies', newFormCompanies, { shouldDirty: true, shouldValidate: true });
        }
        previousSelectedCompanyIds.current = currentSelected;
    }, [selectedCompanyId, eventId, getValues, setValue, groupedCompanyOptions]);
    // Ensure welcome text is never empty on new event (e.g. recover if it was cleared)
    useEffect(() => {
        if (eventId)
            return;
        const current = getValues('welcomeText');
        const isEmpty = !current || !current.trim() || !current.replace(/<[^>]*>/g, '').trim();
        if (isEmpty) {
            setValue('welcomeText', DEFAULT_WELCOME_TEMPLATE);
        }
    }, [eventId, getValues, setValue]);
    // When switching to create mode (no eventId), ensure selected language defaults to primary language from form
    useEffect(() => {
        if (eventId)
            return;
        const primaryLangId = watch('primaryLanguageId');
        if (primaryLangId && selectedLanguageId !== primaryLangId) {
            setSelectedLanguageId(primaryLangId);
        }
    }, [eventId, watch('primaryLanguageId')]);
    // Don't auto-add application language - user must explicitly select all languages
    // Removed automatic language selection logic
    // Keep primary language aligned with selected languages.
    // If exactly one language is selected, auto-set it as primary.
    useEffect(() => {
        const selectedLanguages = watch('languageIds') || [];
        const currentPrimary = watch('primaryLanguageId');
        // Validate that current primary is still in selected languages
        const selectedLangIds = selectedLanguages.map((lang) => {
            const langId = typeof lang === 'object' && 'value' in lang ? lang.value : lang;
            return String(langId);
        });
        // Only clear primary if it was actually removed from selection
        // Don't auto-select a new one - user must explicitly choose
        if (selectedLangIds.length === 1) {
            const onlyLanguageId = selectedLangIds[0];
            if (String(currentPrimary || '') !== onlyLanguageId) {
                setValue('primaryLanguageId', onlyLanguageId);
            }
            return;
        }
        if (selectedLangIds.length > 1 && currentPrimary) {
            // Clear primary only if the previously selected primary was removed.
            if (!selectedLangIds.includes(String(currentPrimary))) {
                setValue('primaryLanguageId', undefined);
            }
        }
        else if (selectedLanguages.length === 0) {
            // No languages selected, clear primary
            setValue('primaryLanguageId', undefined);
        }
    }, [watch('languageIds'), setValue]);
    // Function to update URL with current step
    const updateStepInURL = useCallback((step) => {
        const newSearchParams = new URLSearchParams(window.location.search);
        newSearchParams.set('step', (step + 1).toString()); // Convert to 1-based for URL
        navigate(`/create-event?${newSearchParams.toString()}`, { replace: true });
    }, [navigate]);
    useEffect(() => {
        // Skip when previewing a translation — step names come from the backend translation response
        if (isTranslationPreview)
            return;
        // Default Value Collector
        const loadedLangs = Object.keys(i18n.store?.data || {});
        const allDefaultNames = new Set(defaultStepNames);
        const allDefaultDescs = new Set(defaultStepDescriptions);
        stepKeys.forEach((k) => {
            const tk = k === 'Misc' ? 'misc' : k;
            loadedLangs.forEach((lang) => {
                const tLang = i18n.getFixedT(lang);
                const titleKey = `eventSteps.${tk}Title`;
                const descKey = `eventSteps.${tk}Desc`;
                if (i18n.exists(titleKey, { lng: lang }))
                    allDefaultNames.add(String(tLang(titleKey)));
                if (i18n.exists(descKey, { lng: lang }))
                    allDefaultDescs.add(String(tLang(descKey)));
            });
        });
        // Conditional Updater
        const currentSteps = getValues('steps') || [];
        stepKeys.forEach((key, idx) => {
            if (currentSteps[idx]) {
                const translationKey = key === 'Misc' ? 'misc' : key;
                const titleKey = `eventSteps.${translationKey}Title`;
                const descKey = `eventSteps.${translationKey}Desc`;
                const currentName = String(currentSteps[idx].name ?? '').trim();
                const currentDesc = String(currentSteps[idx].description ?? '').trim();
                if (allDefaultNames.has(currentName)) {
                    setValue(`steps.${idx}.name`, t(titleKey));
                }
                if (i18n.exists(descKey)) {
                    if (!currentDesc || allDefaultDescs.has(currentDesc)) {
                        setValue(`steps.${idx}.description`, t(descKey));
                    }
                }
            }
        });
    }, [i18n.language, t, setValue, getValues, eventId]);
    // Handle URL parameter changes for step persistence
    useEffect(() => {
        // If coming from translations preview (languageId present), force meta mode
        if (eventId && languageIdParam && lockToMetaForPreview) {
            setMode('meta');
            setActiveStep(0);
            const newSearchParams = new URLSearchParams(window.location.search);
            if (newSearchParams.has('step')) {
                newSearchParams.delete('step');
                navigate(`/create-event?${newSearchParams.toString()}`, { replace: true });
            }
            return;
        }
        if (!eventId) {
            // For new events (no eventId), always start in meta mode
            setMode('meta');
            setActiveStep(0);
            // Clear any step parameters from URL for new events
            const newSearchParams = new URLSearchParams(window.location.search);
            newSearchParams.delete('step');
            navigate(`/create-event?${newSearchParams.toString()}`, { replace: true });
        }
        else if (eventId && stepParam) {
            // If we have an eventId and step parameter, we're in steps mode
            setMode('steps');
            const stepNumber = parseInt(stepParam);
            if (!isNaN(stepNumber)) {
                // Ensure step is within valid bounds (1 to total steps)
                // Use actual formSteps length if available, otherwise fall back to noOfSteps
                const currentSteps = watch('steps') || [];
                const totalSteps = currentSteps.length > 0
                    ? currentSteps.length
                    : watch('noOfSteps')
                        ? parseInt(watch('noOfSteps'))
                        : 5;
                const validStep = Math.max(1, Math.min(stepNumber, totalSteps));
                setActiveStep(Math.max(0, validStep - 1)); // Convert to 0-based index
                // Update URL if step was corrected
                if (validStep !== stepNumber) {
                    const newSearchParams = new URLSearchParams(window.location.search);
                    newSearchParams.set('step', validStep.toString());
                    navigate(`/create-event?${newSearchParams.toString()}`, { replace: true });
                }
            }
        }
        else if (eventId && !stepParam) {
            // If we have an eventId but no step parameter, stay in meta form mode
            // Don't automatically switch to steps mode
            setMode('meta');
            setActiveStep(0);
        }
    }, [eventId, stepParam, navigate, watch]);
    // Starting a brand-new event: clear any previously loaded event from Redux so
    // stale data (e.g. the last-viewed event's primary language) doesn't leak into
    // this form's fallbacks before the user has made their own selections.
    useEffect(() => {
        if (eventId)
            return;
        dispatch(resetVDKEvent());
    }, [dispatch, eventId]);
    useEffect(() => {
        if (!eventId)
            return;
        // Prevent duplicate fetches (StrictMode, state churn)
        if (lastFetchedEventIdRef.current === eventId)
            return;
        lastFetchedEventIdRef.current = eventId;
        setIsLoadingEventData(true);
        dispatch(getVDKEventByIdAsync({
            id: eventId,
            documentType: EventDocumentType.BackgroundImage,
            languageId: selectedLanguageId
        }))
            .unwrap()
            .then(applyEventToForm)
            .catch((error) => {
            setIsLoadingEventData(false);
        });
    }, [dispatch, eventId]);
    // Ensure originalLanguageIds is always set from event languages when event is available
    // This is critical for detecting new languages added via sidebar
    useEffect(() => {
        if (!eventId || !vdkEvent || vdkEvent.id !== eventId)
            return;
        const eventLangIds = (vdkEvent.eventLanguages || []).map((l) => l.id);
        if (eventLangIds.length > 0) {
            // Only update if originalLanguageIds is empty or different (to avoid overwriting if already set correctly)
            setOriginalLanguageIds((prev) => {
                // If already set and matches, don't change
                if (prev.length > 0 &&
                    JSON.stringify(prev.sort()) === JSON.stringify(eventLangIds.sort())) {
                    return prev;
                }
                // Otherwise set from event
                return eventLangIds;
            });
        }
    }, [eventId, vdkEvent]);
    // Handle language change from preview: refetch event with selected languageId
    const handlePreviewLanguageChange = (languageId) => {
        setSelectedLanguageId(languageId);
        if (!eventId)
            return;
        dispatch(getVDKEventByIdAsync({
            id: eventId,
            documentType: EventDocumentType.BackgroundImage,
            languageId
        }))
            .unwrap()
            .then(applyEventToForm);
    };
    // When switching to steps in edit flow without a URL language, default to primary language
    useEffect(() => {
        if (mode !== 'steps' || !eventId || hasInitializedStepsLanguage)
            return;
        // Respect languageId from URL (translations preview)
        if (languageIdParam) {
            setHasInitializedStepsLanguage(true);
            return;
        }
        const primary = vdkEvent?.eventLanguages?.find((l) => l.isPrimary);
        if (primary && selectedLanguageId !== primary.id) {
            handlePreviewLanguageChange(primary.id);
        }
        setHasInitializedStepsLanguage(true);
    }, [mode, eventId, vdkEvent, languageIdParam, hasInitializedStepsLanguage, selectedLanguageId]);
    // Reset initialization flag when leaving steps mode or changing event
    useEffect(() => {
        if (mode !== 'steps') {
            setHasInitializedStepsLanguage(false);
        }
    }, [mode, eventId]);
    const formSteps = watch('steps') || [];
    const noOfSteps = watch('noOfSteps');
    const actualTotalSteps = formSteps.length > 0 ? formSteps.length : parseInt(noOfSteps || '0') || 0;
    const isLastStep = activeStep === actualTotalSteps - 1;
    useEffect(() => {
        if (selectedCompanies && selectedCompanies.length > 0) {
            const companyIds = selectedCompanies.map((company) => typeof company === 'object' && company.value ? company.value : company);
            dispatch(getAllUsersByCompany(companyIds));
            dispatch(getAllCollaboratorsByCompanyAsync(companyIds));
        }
    }, [selectedCompanies, dispatch]);
    // When collaborator options change due to company change, remove any selected collaborators no longer available
    useEffect(() => {
        const selected = getValues('collaboratorIds') || [];
        if (!Array.isArray(selected) || selected.length === 0)
            return;
        const validIds = new Set((collaboratorOptions ?? []).map((o) => o.value));
        const filtered = selected.filter((opt) => {
            const value = typeof opt === 'object' && opt.value ? opt.value : opt;
            return validIds.has(value);
        });
        if (filtered.length !== selected.length) {
            setValue('collaboratorIds', filtered);
            clearErrors('collaboratorIds');
        }
    }, [collaboratorOptions, getValues, setValue, clearErrors]);
    const resetForm = () => {
        reset();
        clearErrors();
    };
    const handleSaveMissingUndertakingFiles = () => {
        const errors = {};
        const maxSize = 10 * 1024 * 1024; // 10MB
        // Validate that all required languages have files uploaded and check file sizes
        missingUndertakingLanguageIds.forEach((langId) => {
            const files = missingLanguageFiles[langId];
            if (!files || files.length === 0) {
                const lang = languages?.find((l) => l.id === langId);
                errors[langId] =
                    t('validations.required', {
                        field: t('undertaking.label')
                    }) ||
                        t('undertaking.validation') ||
                        'Please upload an undertaking file.';
            }
            else {
                // Check file size for each file
                files.forEach((file) => {
                    if (file.size > maxSize) {
                        errors[langId] =
                            t('validations.fileSizeTooLarge', {
                                maxSize: '10MB'
                            }) || 'File size must be less than 10MB.';
                    }
                });
            }
        });
        // Set validation errors
        setUndertakingValidationErrors(errors);
        // If there are errors, don't proceed
        if (Object.keys(errors).length > 0) {
            return;
        }
        const allSteps = getValues('steps') || [];
        const fieldIndex = allSteps.findIndex((step) => step.formFields?.some((field) => field.fieldType === 'undertaking'));
        if (fieldIndex !== -1) {
            const stepWithUndertaking = allSteps[fieldIndex];
            const undertakingFieldIndex = stepWithUndertaking.formFields.findIndex((field) => field.fieldType === 'undertaking');
            if (undertakingFieldIndex !== -1) {
                const existingFiles = stepWithUndertaking.formFields[undertakingFieldIndex].undertakingFilesByLanguage || {};
                const updatedByLang = { ...existingFiles };
                Object.entries(missingLanguageFiles).forEach(([langId, files]) => {
                    if (files && files.length > 0) {
                        updatedByLang[langId] = files;
                    }
                });
                setValue(`steps.${fieldIndex}.formFields.${undertakingFieldIndex}.undertakingFilesByLanguage`, updatedByLang);
            }
        }
        setMissingUndertakingDialogOpen(false);
        handleSubmit(onSubmit)();
    };
    const onSubmit = async (data) => {
        if (isSubmittingRef.current)
            return;
        // Permission check as safety measure
        if (eventId && !canUpdateEvents) {
            toast.error(t('common.unauthorized') || 'You do not have permission to update events');
            return;
        }
        if (!eventId && !canCreateEvents) {
            toast.error(t('common.unauthorized') || 'You do not have permission to create events');
            return;
        }
        // Validate that at least one step exists
        if (!data.steps || !Array.isArray(data.steps) || data.steps.length === 0) {
            toast.error(t('validations.required', { field: t('common.eventSteps') }) ||
                'At least one step is required');
            return;
        }
        // Validate maximum 10 steps
        if (data.steps.length > 10) {
            toast.error(t('validations.maxSteps') || 'Maximum 10 steps allowed');
            return;
        }
        try {
            // React-hook-form validation has passed, proceed with submission
            // Validate that all steps have at least one question only when in steps mode
            if (mode === 'steps') {
                const stepsWithNoQuestions = data.steps.filter((step) => !step.formFields || step.formFields.length === 0);
                if (stepsWithNoQuestions.length > 0) {
                    toast.error(t('validations.stepHasNoQuestions'));
                    return;
                }
            }
            if (mode === 'meta' && eventId) {
                const allSteps = data.steps || [];
                const hasUndertakingField = allSteps.some((step) => step.formFields?.some((field) => field.fieldType === 'undertaking'));
                if (hasUndertakingField) {
                    const selectedLanguagesRaw = data.languageIds || [];
                    const selectedLanguageIds = selectedLanguagesRaw.map((lang) => typeof lang === 'object' && 'value' in lang ? lang.value : lang);
                    // Get original language IDs from event if not already set
                    // This handles the case where originalLanguageIds wasn't set yet (e.g., event loaded but form not applied)
                    const eventOriginalLanguageIds = originalLanguageIds.length > 0
                        ? originalLanguageIds.map((id) => String(id))
                        : (vdkEvent?.eventLanguages || []).map((l) => String(l.id));
                    const newLanguageIds = selectedLanguageIds
                        .map((id) => String(id))
                        .filter((langId) => !eventOriginalLanguageIds.includes(langId));
                    if (newLanguageIds.length > 0) {
                        const undertakingFields = allSteps.flatMap((step) => (step.formFields || []).filter((field) => field.fieldType === 'undertaking'));
                        const languagesWithFiles = new Set();
                        undertakingFields.forEach((field) => {
                            if (field.undertakingUploadedFilesByLanguage) {
                                Object.keys(field.undertakingUploadedFilesByLanguage).forEach((langId) => {
                                    languagesWithFiles.add(langId);
                                });
                            }
                            if (field.undertakingFilesByLanguage) {
                                Object.keys(field.undertakingFilesByLanguage).forEach((langId) => {
                                    const files = field.undertakingFilesByLanguage[langId];
                                    if (files && files.length > 0) {
                                        languagesWithFiles.add(langId);
                                    }
                                });
                            }
                        });
                        const languagesWithoutFiles = newLanguageIds.filter((langId) => !languagesWithFiles.has(langId));
                        if (languagesWithoutFiles.length > 0) {
                            const languageNames = languagesWithoutFiles
                                .map((langId) => {
                                const lang = selectedLanguagesRaw.find((l) => (typeof l === 'object' && l.value === langId) || l === langId);
                                return typeof lang === 'object' && 'label' in lang ? lang.label : langId;
                            })
                                .filter(Boolean);
                            setMissingUndertakingLanguages(languageNames);
                            setMissingUndertakingLanguageIds(languagesWithoutFiles);
                            setMissingLanguageFiles({});
                            setMissingUndertakingDialogOpen(true);
                            return;
                        }
                    }
                }
            }
            // Extract company IDs from the selected company objects
            const companyIds = Array.isArray(data.companies)
                ? data.companies.map((company) => typeof company === 'object' && company.value ? company.value : company)
                : [];
            // Extract collaborator IDs from the selected collaborator objects
            const collaboratorIds = Array.isArray(data.collaboratorIds)
                ? data.collaboratorIds.map((collaborator) => typeof collaborator === 'object' && collaborator.value
                    ? collaborator.value
                    : collaborator)
                : [];
            // Extract language IDs from the selected language objects
            // Safety check: ensure data.languageIds is an array before mapping
            const languageIdsRaw = Array.isArray(data.languageIds) ? data.languageIds : [];
            const languageIds = languageIdsRaw.map((lang) => typeof lang === 'object' && lang.value ? lang.value : lang);
            // Safety check: ensure languageIds is an array and has at least one language
            if (!Array.isArray(languageIds) || languageIds.length === 0) {
                toast.error(t('validations.required', { field: t('labels.languages') }) ||
                    'At least one language is required.');
                return;
            }
            // Get primary language ID from form (manually selected by user)
            const primaryLanguageIdFromForm = data.primaryLanguageId
                ? String(data.primaryLanguageId)
                : undefined;
            // Validate that primary language is in the selected languages
            const languageIdsStrings = languageIds.map((id) => String(id));
            let validPrimaryLanguageId = primaryLanguageIdFromForm;
            if (validPrimaryLanguageId && !languageIdsStrings.includes(validPrimaryLanguageId)) {
                // If primary language is not in selected languages, use the first selected language
                validPrimaryLanguageId = String(languageIds[0]);
                toast.warning(t('events.primaryLanguageNotInSelected') ||
                    'Primary language must be one of the selected languages. Using first selected language.');
            }
            else if (!validPrimaryLanguageId && languageIds.length > 0) {
                // If no primary language is set, use the first selected language
                validPrimaryLanguageId = String(languageIds[0]);
            }
            // Use primary language as the selected language for preview/edit (unless user came from translations tab)
            const validSelectedLanguageId = validPrimaryLanguageId;
            if (!isTranslationPreview &&
                validSelectedLanguageId &&
                validSelectedLanguageId !== selectedLanguageId) {
                setSelectedLanguageId(validSelectedLanguageId);
            }
            const interestIds = Array.isArray(data.interests)
                ? data.interests.map((interest) => typeof interest === 'object' && interest.value ? interest.value : interest)
                : [];
            if (isSubmittingRef.current)
                return;
            isSubmittingRef.current = true;
            setIsLoading(true);
            // Prepare background images for upload - separate new and existing images
            let preparedBackgroundImages = [];
            let existingImages = [];
            if (backgroundImages.length > 0) {
                // Separate new uploaded images from existing images
                const newImages = backgroundImages.filter((img) => img.file);
                const existingImageInfos = backgroundImages.filter((img) => {
                    return img.id && !img.file;
                });
                // Process ALL default images (both selected and unselected) - convert them to files for upload
                const defaultImages = backgroundImages.filter((img) => img.isDefaultImage && img.url && !img.file);
                // Convert all default images to files for upload to blob storage
                const converted = await Promise.all(defaultImages.map(async (defaultImg) => {
                    const filename = defaultImg.url.split('/').pop() || 'defaultBg.jpg';
                    const file = await assetToFile(defaultImg.url, filename);
                    return {
                        file,
                        isSelected: defaultImg.isSelected,
                        isBackgroundImage: defaultImg.isBackgroundImage
                    };
                }));
                newImages.push(...converted);
                // Process new images (including converted default images)
                if (newImages.length > 0) {
                    preparedBackgroundImages = await prepareBackgroundImagesForUpload(newImages);
                }
                // Process existing images
                existingImages = existingImageInfos.map((img) => ({
                    id: img.id,
                    isBackgroundImage: Boolean(img.isSelected || img.isBackgroundImage),
                    isCustomImage: Boolean(img.isCustomImage)
                }));
            }
            if (eventId && mode === 'meta') {
                // Extract undertaking files before serializing formSteps
                const undertakingPairs = [];
                const stepsArray = Array.isArray(data.steps) ? data.steps : [];
                stepsArray.forEach((step) => {
                    const formFields = Array.isArray(step.formFields) ? step.formFields : [];
                    formFields.forEach((field) => {
                        if (field.fieldType === 'undertaking') {
                            const byLang = field.undertakingFilesByLanguage || {};
                            Object.entries(byLang).forEach(([langId, files]) => {
                                if (Array.isArray(files) && files.length > 0 && files[0] instanceof File) {
                                    undertakingPairs.push({ file: files[0], languageId: langId });
                                }
                            });
                        }
                    });
                });
                const undertakingFiles = undertakingPairs.map((p) => p.file);
                const undertakingFilesLanguages = undertakingPairs.map((p) => p.languageId);
                // Update existing event
                const updateEventRequest = {
                    id: eventId,
                    title: data.eventTitle,
                    location: (data.location ?? '').trim() || null,
                    assigneeIds: data.assigneeIds?.map((user) => (typeof user === 'string' ? user : user.value)) ||
                        undefined,
                    type: EventTypeEnum.All,
                    startDate: data.eventDuration.startDate,
                    endDate: data.eventDuration.endDate,
                    isMultiDayEvent: data.isMultiDayEvent,
                    startTime: data.startTime || undefined,
                    endTime: data.endTime || undefined,
                    dueRegistrationDate: data.eventDuration.endDate,
                    status: vdkEvent?.status,
                    isExternalAllowed: data.externalsAllowed,
                    noOfSteps: parseInt(data.noOfSteps),
                    welcomeText: data.welcomeText,
                    thankYouText: data.thankYouText,
                    contactEventTeamPhone: data.contactEventTeamPhone || null,
                    contactEventTeamEmail: data.contactEventTeamEmail || null,
                    details: data.details,
                    floorPlan: data.floorPlan,
                    isPasswordProtection: data.passwordProtection,
                    password: data.passwordProtection ? data.password : undefined,
                    eventCompanyIds: companyIds,
                    collaboratorIds: collaboratorIds,
                    languageIds: languageIds,
                    // In the eventId && mode === 'meta' update path — same fix
                    formSteps: JSON.stringify((Array.isArray(data.steps) ? data.steps : []).map((step, index) => ({
                        id: step.id ? step.id : null,
                        name: step.name,
                        description: step.description ?? '',
                        sortOrder: index + 1,
                        formFields: (Array.isArray(step.formFields) ? step.formFields : []).map((field) => {
                            const { undertakingFilesByLanguage, ...rest } = field;
                            return {
                                ...rest
                            };
                        })
                    }))),
                    extraInformations: extraInformationRows.length > 0
                        ? extraInformationRows.map((row, index) => ({
                            id: row.id,
                            title: row.title,
                            description: row.description,
                            sortOrder: index + 1,
                            file: row.file ?? null
                        }))
                        : undefined,
                    backgroundImages: preparedBackgroundImages,
                    existingImages: existingImages,
                    interestIds: interestIds,
                    languageId: isTranslationPreview
                        ? selectedLanguageId || undefined
                        : validSelectedLanguageId || undefined,
                    isSponsorPartnerEvent: vdkEvent?.isSponsorPartnerEvent,
                    isSponsorPartnerExternalEvent: vdkEvent?.isSponsorPartnerExternalEvent,
                    benefitType: vdkEvent?.benefitType,
                    isTranslationPreview: isTranslationPreview,
                    undertakingFiles: undertakingFiles.length > 0 ? undertakingFiles : undefined,
                    undertakingFilesLanguages: undertakingFilesLanguages.length > 0 ? undertakingFilesLanguages : undefined
                };
                await dispatch(updateVDKEventAsync(updateEventRequest)).unwrap();
                toast.success(t('events.eventUpdatedSuccessfully') || 'Event updated successfully!');
                // Immediately refetch updated event to get latest languages/primary and apply to form
                await dispatch(getVDKEventByIdAsync({
                    id: eventId,
                    documentType: EventDocumentType.BackgroundImage,
                    languageId: isTranslationPreview ? selectedLanguageId : undefined
                }))
                    .unwrap()
                    .then((ev) => {
                    if (!isTranslationPreview) {
                        // Use primary language from form (which was just saved)
                        const primaryLangId = getValues('primaryLanguageId');
                        if (primaryLangId) {
                            setSelectedLanguageId(String(primaryLangId));
                        }
                        else {
                            // Fallback to primary from event if form doesn't have it
                            const primary = Array.isArray(ev?.eventLanguages)
                                ? ev.eventLanguages.find((l) => l.isPrimary) || ev.eventLanguages[0]
                                : undefined;
                            if (primary?.id && selectedLanguageId !== primary.id) {
                                setSelectedLanguageId(primary.id);
                            }
                        }
                    }
                    applyEventToForm(ev);
                })
                    .catch(() => { });
            }
            else {
                // Create new event
                const createEventRequest = {
                    title: data.eventTitle,
                    eventCode: '',
                    location: (data.location ?? '').trim() || null,
                    assigneeIds: data.assigneeIds?.map((user) => (typeof user === 'string' ? user : user.value)) ||
                        undefined,
                    type: EventTypeEnum.All,
                    startDate: data.eventDuration.startDate,
                    endDate: data.eventDuration.endDate,
                    isMultiDayEvent: data.isMultiDayEvent,
                    startTime: data.startTime || undefined,
                    endTime: data.endTime || undefined,
                    dueRegistrationDate: data.eventDuration.endDate,
                    status: EventStatusEnum.Scheduled,
                    isExternalAllowed: data.externalsAllowed,
                    noOfSteps: parseInt(data.noOfSteps),
                    welcomeText: data.welcomeText,
                    thankYouText: data.thankYouText,
                    contactEventTeamPhone: data.contactEventTeamPhone || null,
                    contactEventTeamEmail: data.contactEventTeamEmail || null,
                    details: data.details,
                    floorPlan: data.floorPlan,
                    isPasswordProtection: data.passwordProtection,
                    password: data.passwordProtection ? data.password : undefined,
                    eventCompanyIds: companyIds,
                    collaboratorIds: collaboratorIds,
                    languageIds: languageIds,
                    languageId: validSelectedLanguageId || undefined,
                    formSteps: JSON.stringify((Array.isArray(data.steps) ? data.steps : []).map((step, index) => ({
                        id: step.id ? step.id : null,
                        name: step.name,
                        description: step.description ?? '',
                        sortOrder: index + 1,
                        formFields: Array.isArray(step.formFields) ? step.formFields : []
                    }))),
                    backgroundImages: preparedBackgroundImages,
                    interestIds: interestIds,
                    extraInformations: extraInformationRows.length > 0
                        ? extraInformationRows.map((row, index) => ({
                            id: row.id,
                            title: row.title,
                            description: row.description,
                            sortOrder: index + 1,
                            file: row.file ?? null
                        }))
                        : undefined,
                    isSponsorPartnerEvent: false,
                    isSponsorPartnerExternalEvent: false
                };
                const response = await dispatch(createVDKEventAsync(createEventRequest))
                    .unwrap()
                    .then(async (response) => {
                    if (response) {
                        // Create default email templates for the new event
                        const defaultTemplates = buildDefaultEmailTemplates(languages);
                        await dispatch(createDefaultEmailTemplatesAsync({
                            eventId: response.id,
                            templates: defaultTemplates
                        }))
                            .unwrap()
                            .catch((templateError) => {
                            console.warn('Failed to create default email templates:', templateError);
                        });
                        toast.success(t('events.eventCreatedSuccessfully') || 'Event created successfully!');
                        navigate(`/create-event?eventId=${response.id}`, { replace: true });
                    }
                });
            }
            // Submission succeeded, so any step removal is now confirmed by the backend
            lastRemovedStepsSnapshotRef.current = null;
            // Switch to steps mode after successful create/update and refetch
            setMode('steps');
            setActiveStep(0);
            updateStepInURL(0);
        }
        catch (error) {
            // Restore any step removed just before this submission, since the backend
            // rejected the change (e.g. step has existing responses)
            if (lastRemovedStepsSnapshotRef.current) {
                setValue('steps', lastRemovedStepsSnapshotRef.current, { shouldDirty: false });
                lastRemovedStepsSnapshotRef.current = null;
            }
            const errorMessage = error?.message ||
                error?.error ||
                error?.data?.message ||
                t('events.eventCreationFailed') ||
                'Failed to create event. Please try again.';
            toast.error(errorMessage);
        }
        finally {
            isSubmittingRef.current = false;
            setIsLoading(false);
        }
    };
    const handleSubmitFormStep = async (payload) => {
        const stepToSubmit = formSteps.find((x) => x.sortOrder === activeStep + 1);
        if (stepToSubmit) {
            const stepName = watch(`steps.${activeStep}.name`);
            if (!stepName || stepName.trim() === '') {
                return;
            }
            // Check if the current step has any form fields only when in steps mode
            if (mode === 'steps') {
                const currentStepFormFields = watch(`steps.${activeStep}.formFields`) || [];
                if (currentStepFormFields.length === 0) {
                    // Show error message for empty step
                    toast.error(t('validations.stepHasNoQuestions'));
                    return;
                }
                // Validate all form field labels are not empty
                const fieldsWithEmptyLabels = currentStepFormFields.filter((field) => !field.label || field.label.trim() === '');
                // Validate all companyDropdownFields (Referred By) labels are not empty
                const fieldsWithEmptyReferredByLabels = currentStepFormFields.filter((field) => {
                    if (field.fieldType === 'companyDropdown' && field.companyDropdownFields) {
                        return field.companyDropdownFields.some((subField) => !subField.label || subField.label.trim() === '');
                    }
                    return false;
                });
                // Validate all option labels (children of parent fields) are not empty
                const fieldsWithEmptyOptionLabels = currentStepFormFields.filter((field) => {
                    const optionTypes = ['radio', 'choice', 'checkbox-group', 'dropdown', 'multi-select'];
                    if (optionTypes.includes(field.fieldType) && field.options) {
                        return field.options.some((opt) => !opt.label || opt.label.trim() === '');
                    }
                    return false;
                });
                if (fieldsWithEmptyLabels.length > 0 ||
                    fieldsWithEmptyReferredByLabels.length > 0 ||
                    fieldsWithEmptyOptionLabels.length > 0) {
                    // Set error on fields with empty labels
                    const updatedFields = currentStepFormFields.map((field) => {
                        const hasEmptyLabel = !field.label || field.label.trim() === '';
                        const hasEmptyReferredByLabels = field.fieldType === 'companyDropdown' &&
                            field.companyDropdownFields?.some((subField) => !subField.label || subField.label.trim() === '');
                        const optionTypes = ['radio', 'choice', 'checkbox-group', 'dropdown', 'multi-select'];
                        const hasEmptyOptionLabels = optionTypes.includes(field.fieldType) &&
                            field.options?.some((opt) => !opt.label || opt.label.trim() === '');
                        if (hasEmptyLabel) {
                            return {
                                ...field,
                                error: 'validations.required',
                                errorField: t('labels.fieldLabel') || 'Field label'
                            };
                        }
                        if (hasEmptyReferredByLabels) {
                            return {
                                ...field,
                                companyDropdownFields: field.companyDropdownFields?.map((subField) => {
                                    const isEmpty = !subField.label || subField.label.trim() === '';
                                    if (isEmpty && !subField.error) {
                                        return {
                                            ...subField,
                                            error: 'validations.required',
                                            errorField: t('labels.fieldLabel') || 'Field label'
                                        };
                                    }
                                    return subField;
                                })
                            };
                        }
                        if (hasEmptyOptionLabels) {
                            return {
                                ...field,
                                options: field.options?.map((opt) => {
                                    const isEmpty = !opt.label || opt.label.trim() === '';
                                    if (isEmpty && !opt.error) {
                                        return {
                                            ...opt,
                                            error: 'validations.required',
                                            errorField: t('labels.fieldLabel') || 'Field label'
                                        };
                                    }
                                    return opt;
                                })
                            };
                        }
                        return field;
                    });
                    setValue(`steps.${activeStep}.formFields`, updatedFields);
                    return;
                }
            }
            // Calculate global field order across all steps
            const allSteps = watch('steps') || [];
            const currentStepIndex = allSteps.findIndex((step) => step.id === stepToSubmit.id);
            const fieldsInPreviousSteps = allSteps
                .slice(0, currentStepIndex)
                .reduce((total, step) => total + (step.formFields?.length || 0), 0);
            const mergeTranslations = (translations, languageId, label, descriptionForToolTip) => {
                if (!languageId)
                    return translations || [];
                const existing = translations || [];
                const idx = existing.findIndex((t) => t.languageId === languageId || t.languageCode === languageId);
                if (idx >= 0) {
                    const updated = [...existing];
                    updated[idx] = { ...updated[idx], languageId, label, descriptionForToolTip };
                    return updated;
                }
                return [
                    ...existing.map((t) => {
                        // normalize old key if present
                        if ('languageCode' in t && !('languageId' in t)) {
                            const { languageCode, ...rest } = t;
                            return { languageId: languageCode, ...rest };
                        }
                        return t;
                    }),
                    { languageId, label, descriptionForToolTip }
                ];
            };
            const mergeUndertakingTranslations = (translations, languageId, undertakingToggleLabel, undertakingAlertNote) => {
                if (!languageId)
                    return translations || [];
                const existing = translations || [];
                const normalize = (t) => {
                    if ('languageCode' in t && !('languageId' in t)) {
                        const { languageCode, ...rest } = t;
                        return { languageId: languageCode, ...rest };
                    }
                    return t;
                };
                const idx = existing.findIndex((t) => t.languageId === languageId || t.languageCode === languageId);
                const entry = {
                    languageId,
                    undertakingToggleLabel: undertakingToggleLabel || '',
                    undertakingAlertNote: undertakingAlertNote || ''
                };
                if (idx >= 0) {
                    const updated = existing.map((t, i) => i === idx ? { ...normalize(t), ...entry } : normalize(t));
                    return updated;
                }
                return [...existing.map(normalize), entry];
            };
            const formFields = stepToSubmit.formFields.flatMap((field, index) => {
                const mainField = {
                    ...(field.id && { id: field.id }),
                    isFromBackend: Boolean(field.isFromBackend),
                    formStepName: stepToSubmit.name,
                    description: stepToSubmit.description,
                    label: field.label,
                    descriptionForToolTip: field.descriptionForToolTip,
                    labelTranslations: mergeTranslations(field.labelTranslations, selectedLanguageId, field.label, field.descriptionForToolTip),
                    undertakingTranslations: mergeUndertakingTranslations(field.undertakingTranslations, selectedLanguageId, field.undertakingToggleLabel, field.undertakingAlertNote),
                    fieldType: getFieldTypeNumber(field.fieldType).toString(),
                    isRequired: field.fieldType === 'toggle' ? false : field.required,
                    isIdentity: field.isIdentity,
                    isUniqueEmail: field.isUniqueEmail,
                    isUniquePhoneNumber: field.isUniquePhoneNumber,
                    formStepId: stepToSubmit.id,
                    fieldParent: field.fieldParent ?? '',
                    fieldChild: field.fieldChild ?? '',
                    fieldAdditionalNote: field.fieldAdditionalNote,
                    fieldOrder: fieldsInPreviousSteps + index + 1,
                    undertakingToggleLabel: field.undertakingToggleLabel,
                    undertakingAlertNote: field.undertakingAlertNote,
                    options: field.options?.map((option) => ({
                        id: option.id,
                        label: option.label,
                        labelTranslations: mergeTranslations(option.labelTranslations, selectedLanguageId, option.label),
                        value: option.value
                    })) ?? [],
                    ...(field.fieldType === 'companyDropdown' && field.companyDropdownFields
                        ? {
                            companyDropdownFields: field.companyDropdownFields.map((cdf) => ({
                                id: cdf.id,
                                backendId: cdf.backendId,
                                label: cdf.label,
                                enabled: cdf.enabled,
                                required: cdf.required,
                                labelTranslations: mergeTranslations(cdf.labelTranslations, selectedLanguageId, cdf.label)
                            }))
                        }
                        : {}),
                    ...(field.fieldType === 'additionalAttendees'
                        ? {
                            maxAdditionalAttendees: field.maxAdditionalAttendees ?? 2,
                            maxAdditionalAttendeesReachedMessage: field.maxAdditionalAttendeesReachedMessage ?? ''
                        }
                        : {}),
                    parentOptionId: field.parentOptionId ?? null,
                    parentOptionIds: field.parentOptionIds ?? [],
                    parentFieldId: field.parentFieldId ?? null,
                    parentFieldLabel: field.parentFieldLabel ?? null,
                    parentOptionValue: field.parentOptionValue ?? null,
                    isBranchingEnabled: field.isBranchingEnabled ?? false,
                    relatedFormFieldId: field.relatedFormFieldId,
                    isEnabled: field.isEnabled
                };
                // Additional Attendees: sub-questions are NOT nested config - they're real sibling
                // FormFields (RelatedFormFieldId = this field's id), reusing the exact same generic
                // field/option/translation machinery as any other question. Flatten them here, mirroring
                // how mapFormFieldsToApi already treats every field generically.
                if (field.fieldType !== 'additionalAttendees' || !field.additionalAttendeeFields?.length) {
                    return [mainField];
                }
                const subFields = field.additionalAttendeeFields.map((subField) => ({
                    id: subField.id,
                    isFromBackend: Boolean(subField.isFromBackend),
                    formStepName: stepToSubmit.name,
                    description: stepToSubmit.description,
                    label: subField.label,
                    descriptionForToolTip: '',
                    labelTranslations: [],
                    undertakingTranslations: [],
                    fieldType: getFieldTypeNumber(subField.fieldType).toString(),
                    isRequired: Boolean(subField.required),
                    isIdentity: false,
                    isUniqueEmail: false,
                    isUniquePhoneNumber: false,
                    formStepId: stepToSubmit.id,
                    fieldParent: subField.fieldParent ?? '',
                    fieldChild: subField.fieldChild ?? '',
                    fieldAdditionalNote: '',
                    fieldOrder: subField.fieldOrder ?? 1,
                    undertakingToggleLabel: '',
                    undertakingAlertNote: '',
                    options: (subField.options || []).map((option) => ({
                        id: option.id,
                        label: option.label,
                        labelTranslations: [],
                        value: option.value
                    })),
                    parentOptionId: null,
                    parentOptionIds: [],
                    parentFieldId: null,
                    parentFieldLabel: null,
                    parentOptionValue: null,
                    isBranchingEnabled: false,
                    relatedFormFieldId: field.id,
                    subFieldRole: subField.role,
                    isEnabled: true
                }));
                return [mainField, ...subFields];
            });
            // Collect undertaking files for upload (supports per-language files) and align languages
            const undertakingPairs = [];
            stepToSubmit.formFields
                .filter((field) => field.fieldType === 'undertaking')
                .forEach((field) => {
                const byLang = field.undertakingFilesByLanguage || {};
                Object.entries(byLang).forEach(([langId, files]) => {
                    if (Array.isArray(files) && files.length > 0) {
                        // single file per language enforced in UI; take first
                        undertakingPairs.push({ file: files[0], languageId: langId });
                    }
                });
                // Fallback to legacy single file if present and no per-language files were added
                if (undertakingPairs.length === 0 && field.fileData instanceof File) {
                    const selectedLangIdsFallback = (getValues('languageIds') || []);
                    const fallbackLangId = (selectedLangIdsFallback[0] &&
                        (selectedLangIdsFallback[0].value || selectedLangIdsFallback[0])) ||
                        '';
                    undertakingPairs.push({ file: field.fileData, languageId: String(fallbackLangId) });
                }
            });
            const undertakingFiles = undertakingPairs.map((p) => p.file);
            const undertakingFilesLanguages = undertakingPairs.map((p) => p.languageId);
            // Validate: when undertaking field is required, each selected language must have a file
            const hasUndertakingFile = stepToSubmit.formFields
                .filter((field) => field.fieldType === 'undertaking')
                .every((field) => {
                const byLang = field.undertakingFilesByLanguage || {};
                const uploadedByLang = field.undertakingUploadedFilesByLanguage || {};
                const selectedLangIdsRaw = getValues('languageIds') || [];
                const selectedLangIds = selectedLangIdsRaw.map((lang) => typeof lang === 'object' && lang.value ? lang.value : lang);
                if (!field.required)
                    return true;
                // all selected languages must have at least one file
                return selectedLangIds.every((langId) => {
                    const hasNew = Array.isArray(byLang[langId]) && byLang[langId].length > 0;
                    const hasExisting = Array.isArray(uploadedByLang[langId]) && uploadedByLang[langId].length > 0;
                    return hasNew || hasExisting;
                });
            });
            if (stepToSubmit.formFields.filter((field) => field.fieldType === 'undertaking').length > 0 &&
                !hasUndertakingFile) {
                const undertakingField = stepToSubmit.formFields.filter((field) => field.fieldType === 'undertaking')[0];
                // Set error state on the field
                const fieldIndex = stepToSubmit.formFields.findIndex((f) => f.id === undertakingField.id);
                const updatedFields = [...stepToSubmit.formFields];
                updatedFields[fieldIndex] = {
                    ...updatedFields[fieldIndex],
                    error: 'validations.required',
                    errorField: undertakingField.label || 'Undertaking document'
                };
                setValue(`steps.${activeStep}.formFields`, updatedFields);
                setUndertakingDialogOpen(true);
                return;
            }
            let preparedBackgroundImages = [];
            let existingImages = [];
            if (backgroundImages.length > 0) {
                const newImages = backgroundImages.filter((img) => img.file);
                const existingImageInfos = backgroundImages.filter((img) => {
                    return img.id && !img.file;
                });
                // Process ALL default images (both selected and unselected) - convert them to files for upload
                const defaultImages = backgroundImages.filter((img) => img.isDefaultImage && img.url && !img.file);
                // Convert all default images to files for upload to blob storage
                const converted = await Promise.all(defaultImages.map(async (defaultImg) => {
                    const filename = defaultImg.url.split('/').pop() || 'defaultBg.jpg';
                    const file = await assetToFile(defaultImg.url, filename);
                    return {
                        file,
                        isSelected: defaultImg.isSelected,
                        isBackgroundImage: defaultImg.isBackgroundImage
                    };
                }));
                newImages.push(...converted);
                // Process new images (including converted default images)
                if (newImages.length > 0) {
                    preparedBackgroundImages = await prepareBackgroundImagesForUpload(newImages);
                }
                // Process existing images
                existingImages = existingImageInfos.map((img) => ({
                    id: img.id,
                    isBackgroundImage: Boolean(img.isSelected || img.isBackgroundImage)
                }));
            }
            // const newFields = formFields.filter((field) => !field.isFromBackend);
            // const existingFields = formFields.filter((field) => field.isFromBackend && field.id);
            const existingFields = formFields.filter((field) => field.id);
            const newFields = formFields.filter((newField) => {
                return !existingFields?.some((existingField) => existingField.id === newField.id);
            });
            // Create promises for both operations
            const promises = [];
            // Create new fields if any
            if (newFields.length > 0) {
                const createPayload = {
                    formFields: newFields.map((field) => ({
                        ...field,
                        descriptionForToolTip: field.descriptionForToolTip
                    })),
                    backgroundImages: preparedBackgroundImages,
                    existingImages: existingImages,
                    undertakingFiles: undertakingFiles,
                    undertakingFilesLanguages: undertakingFilesLanguages,
                    eventId: eventId
                };
                promises.push(dispatch(createFormFieldAsync(createPayload)).unwrap());
            }
            // Update existing fields if any
            if (existingFields.length > 0) {
                const updatePayload = {
                    formFields: existingFields.map((field) => ({
                        ...field,
                        labelTranslations: mergeTranslations(field.labelTranslations, selectedLanguageId, field.label, field.descriptionForToolTip),
                        descriptionForToolTip: field.descriptionForToolTip || '',
                        undertakingTranslations: mergeUndertakingTranslations(field.undertakingTranslations, selectedLanguageId, field.undertakingToggleLabel, field.undertakingAlertNote),
                        // Ensure options have proper structure for updates
                        options: field.options?.map((option) => ({
                            id: option.id || '',
                            label: option.label,
                            labelTranslations: mergeTranslations(option.labelTranslations, selectedLanguageId, option.label),
                            value: option.value
                        })) || [],
                        // Include companyDropdownFields with merged translations for updates
                        ...(field.fieldType === 'companyDropdown' && field.companyDropdownFields
                            ? {
                                companyDropdownFields: field.companyDropdownFields.map((cdf) => ({
                                    id: cdf.id,
                                    backendId: cdf.backendId,
                                    label: cdf.label,
                                    enabled: cdf.enabled,
                                    required: cdf.required,
                                    labelTranslations: mergeTranslations(cdf.labelTranslations, selectedLanguageId, cdf.label)
                                }))
                            }
                            : {})
                    })),
                    backgroundImages: preparedBackgroundImages,
                    existingImages: existingImages,
                    undertakingFiles: undertakingFiles,
                    undertakingFilesLanguages: undertakingFilesLanguages,
                    eventId: eventId
                };
                promises.push(dispatch(updateFormFieldAsync(updatePayload)).unwrap());
            }
            // Execute all operations
            Promise.all(promises)
                .then((results) => {
                const hasResults = results.some((result) => result);
                if (hasResults) {
                    if (isLastStep) {
                        setShowSuccessScreen(true);
                        return;
                    }
                    const newStep = activeStep + 1;
                    setActiveStep(newStep);
                    updateStepInURL(newStep);
                    // Store a deep-cloned snapshot of the synced values so we can compare
                    // the user's current form values against this baseline later.
                    try {
                        setDuplicateVdkEvent(cloneDeep(getValues()));
                    }
                    catch (e) {
                        // ignore cloning errors
                        setDuplicateVdkEvent(undefined);
                    }
                }
            })
                .catch((e) => {
                toast.error(e.error);
            });
        }
    };
    const handleNext = () => {
        handleSubmitFormStep({}); // Empty payload since we handle the logic inside handleSubmitFormStep
    };
    const handleBack = () => {
        const newStep = activeStep - 1;
        setActiveStep(newStep);
        updateStepInURL(newStep);
    };
    // Deep compare current form values with the synced snapshot captured when
    // the backend event was applied to the form. If snapshot not available,
    // treat as no unsaved changes (preview button stays disabled until event loaded).
    const haveUnsavedChanges = useMemo(() => {
        if (!duplicateVdkEvent)
            return false;
        const currentFormData = getValues();
        // Deep equality check - any difference between snapshot and current form means unsaved changes
        return !isEqual(duplicateVdkEvent, currentFormData);
    }, [duplicateVdkEvent, watch()]);
    const handleNavigateToPreviewEventForm = () => {
        if (haveUnsavedChanges) {
            setShowDiscardChangesPopup(true);
        }
        else {
            setShowDiscardChangesPopup(false);
            navigate(`/event/preview/${eventId}`);
        }
    };
    // Check if current step has questions only when in steps mode
    const currentStepFormFields = watch(`steps.${activeStep}.formFields`) || [];
    const hasQuestionsInCurrentStep = mode === 'steps' ? currentStepFormFields.length > 0 : true;
    const styles = useCreateEventStyles(mode, tabValue);
    return showSuccessScreen ? (_jsx(SuccessPage, { eventTitle: getValues('eventTitle'), eventId: eventId, createdBy: currentUser?.name, memberType: getValues('externalsAllowed')
            ? t('events.internalAndExternal')
            : t('events.internal') })) : (_jsxs(PageLayout, { title: eventId
            ? isTranslationPreview
                ? t('events.editTranslation')
                : t('events.editEvent')
            : t('events.newEvent'), 
        // Always show a back button when previewing a translation so user can return to Translations tab
        showBackButton: isSmallScreen || isTranslationPreview, onBackClick: () => isTranslationPreview
            ? navigate(eventId ? `/event-details/${eventId}?tab=translations` : '/')
            : navigate(eventId ? `/event-details/${eventId}` : '/'), showLeftIcon: true, leftIcon: _jsx(MagicStar, { variant: 'Outline', width: 32, height: 32 }), secondTitle: t('events.planYourEvent'), showTabs: isSmallScreen, showBreadCrumbs: Boolean(eventId) && isTranslationPreview, customBreadcrumbs: eventId && isTranslationPreview
            ? [
                { label: t('common.events'), path: '/events' },
                {
                    label: vdkEvent?.title || t('events.eventDetails'),
                    path: `/event-details/${eventId}`
                },
                { label: t('events.editTranslation') }
            ]
            : undefined, tabs: _jsxs(Tabs, { onChange: (_, newValue) => setTabValue(newValue), variant: 'fullWidth', value: tabValue, sx: { width: '100%' }, children: [_jsx(Tab, { label: t('events.form'), value: 'form' }), _jsx(Tab, { label: t('actions.style'), value: 'style' }), _jsx(Tab, { label: t('actions.preview'), value: 'preview' })] }), rightContent: _jsx(Button, { color: 'primary', variant: 'contained', size: 'large', onClick: handleNavigateToPreviewEventForm, startIcon: _jsx(Icon, { icon: 'hugeicons:computer-phone-sync', width: 26, height: 26 }), disabled: !eventId, children: t('events.createNewEvent.previewEventForm') }), children: [_jsxs(Box, { id: 'page__create-event', sx: { ...styles.main }, children: [_jsx(TabContext, { value: tabValue, children: _jsx(Box, { className: 'content-container', children: _jsxs(Grid, { container: true, size: 12, spacing: 2, children: [_jsxs(Grid, { size: { xs: 12, lg: 12, xl: 8 }, sx: { position: 'relative' }, children: [_jsxs(Box, { ref: formContainerRef, component: 'form', className: 'form-container', sx: { ...styles.formContainer }, onSubmit: handleSubmit(onSubmit), children: [mode === 'meta' && (!isSmallScreen || tabValue === 'form') && (_jsx(EventMetaForm, { control: control, watch: watch, setValue: setValue, errors: errors, userOptions: userOptions, companyOptions: groupedCompanyOptions, registeredCompanyIds: registeredCompanyIds, collaboratorOptions: collaboratorOptions ?? [], interestOptions: interestOptions, languageOptions: languageOptions, eventLanguages: languages, isEventDataLoaded: isEventDataLoaded, isLoadingEventData: isLoadingEventData, isMetaLocked: Boolean(languageIdParam), backgroundImages: backgroundImages, onBackgroundImagesChange: setBackgroundImages, onExtraInformationChange: setExtraInformationRows, initialExtraInformationRows: eventId ? extraInformationRows : [], translationLanguageCode: translationLanguageCode, onBeforeStepRemove: (stepsBeforeRemoval) => {
                                                            // Only capture the snapshot for the first removal in a batch, so
                                                            // restoring after a failed submit brings back ALL removed steps,
                                                            // not just the one removed right before the submit
                                                            if (!lastRemovedStepsSnapshotRef.current) {
                                                                lastRemovedStepsSnapshotRef.current = stepsBeforeRemoval;
                                                            }
                                                        } })), mode === 'steps' && eventId && (!isSmallScreen || tabValue === 'form') && (_jsx(DynamicForm, { watch: watch, setValue: setValue, totalSteps: actualTotalSteps, currentStep: activeStep + 1, step: formSteps[activeStep], eventId: eventId, eventHasRegistration: eventHasRegistration, errors: errors, isPrimaryLanguage: isPrimaryLanguage, allowStructureEdit: !Boolean(languageIdParam), originalLanguageIds: originalLanguageIds, translationLanguageCode: translationLanguageCode })), isSmallScreen && (_jsxs(_Fragment, { children: [_jsx(TabPanel, { value: 'style', sx: { p: 1 }, children: _jsx(Box, { sx: { ...cardstyles.card, padding: 2, borderRadius: 4 }, children: _jsx(CardContent, { sx: { ...cardstyles.cardContentContainer }, children: _jsx(StartScreenContent, { watch: watch, backgroundImages: backgroundImages, onBackgroundImagesChange: setBackgroundImages }) }) }) }), _jsx(TabPanel, { value: 'preview', sx: { p: 1 }, children: _jsx(Box, { sx: { ...cardstyles.card, padding: 2, borderRadius: 4 }, children: _jsx(CardContent, { sx: { ...cardstyles.cardContentContainer }, children: _jsx(EventPreview, { watch: watch, backgroundImages: backgroundImages }) }) }) })] }))] }), tabValue === 'form' && (_jsx(FloatingActionButton, { bg: 'light', floating: false, buttonText: mode === 'steps' && isLastStep
                                                    ? eventId
                                                        ? t('actions.updateEventNow')
                                                        : t('actions.createEventNow')
                                                    : t('actions.saveAndNext'), startIcon: mode === 'steps' && isLastStep ? _jsx(CheckmarkFilled, { fontSize: '26' }) : _jsx(_Fragment, {}), endIcon: !isLastStep ? _jsx(ArrowForward, { fontSize: 'large' }) : _jsx(_Fragment, {}), onClick: () => {
                                                    if (isSubmittingRef.current || isLoading)
                                                        return;
                                                    if (mode === 'meta' && eventId) {
                                                        handleSubmit(async (data) => {
                                                            // Allow navigation to steps even when coming from translations preview
                                                            setLockToMetaForPreview(false);
                                                            // Do NOT switch to steps yet; wait for update and refetch inside onSubmit
                                                            await onSubmit(data);
                                                        })();
                                                    }
                                                    else if (mode === 'steps') {
                                                        handleNext();
                                                    }
                                                    else {
                                                        handleSubmit(onSubmit)();
                                                    }
                                                }, disabled: (mode === 'steps' && !hasQuestionsInCurrentStep) ||
                                                    isLoading ||
                                                    (eventId ? !canUpdateEvents : !canCreateEvents), additionalButton: mode === 'steps' ? (_jsx(Tooltip, { title: t('actions.back'), arrow: true, children: _jsx(Button, { variant: 'outlined', color: 'primary', size: 'large', startIcon: _jsx(ArrowBack, {}), onClick: () => {
                                                            if (mode === 'steps' && activeStep > 0) {
                                                                handleBack();
                                                            }
                                                            else if (mode === 'steps' && activeStep === 0) {
                                                                setMode('meta');
                                                                setActiveStep(0);
                                                                const newSearchParams = new URLSearchParams(window.location.search);
                                                                newSearchParams.delete('step');
                                                                navigate(`/create-event?${newSearchParams.toString()}`, {
                                                                    replace: true
                                                                });
                                                            }
                                                            else {
                                                                setMode('meta');
                                                            }
                                                        }, sx: {
                                                            borderRadius: '20px',
                                                            padding: '12px 24px',
                                                            fontSize: 'clamp(14px, 2vw, 16px)',
                                                            fontWeight: 400,
                                                            textTransform: 'none',
                                                            whiteSpace: 'nowrap',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            backgroundColor: theme.palette.common.white
                                                        }, children: t('actions.back') }) })) : null, 
                                                // cancelButton={
                                                //   !isSmallScreen ? (
                                                //     <Tooltip title={t('actions.cancelEvent')} arrow>
                                                //       <Button
                                                //         variant='outlined'
                                                //         size='large'
                                                //         onClick={() => navigate(eventId ? `/event-details/${eventId}` : '/')}
                                                //         sx={{
                                                //           backgroundColor: theme.palette.background.paper,
                                                //           color: theme.palette.primary.darker,
                                                //           border: `1px solid ${theme.palette.primary.darker}`,
                                                //           borderRadius: '20px',
                                                //           padding: '12px 24px',
                                                //           fontSize: 'clamp(14px, 2vw, 16px)',
                                                //           fontWeight: 400,
                                                //           textTransform: 'none',
                                                //           whiteSpace: 'nowrap',
                                                //           overflow: 'hidden',
                                                //           textOverflow: 'ellipsis',
                                                //           '&:hover': {
                                                //             backgroundColor: theme.palette.grey[100],
                                                //             color: theme.palette.primary.darker,
                                                //             borderColor: theme.palette.primary.darker
                                                //           }
                                                //         }}
                                                //       >
                                                //         {t('actions.cancelEvent')}
                                                //       </Button>
                                                //     </Tooltip>
                                                //   ) : null
                                                // }
                                                sx: isSmallScreen
                                                    ? {
                                                        position: 'fixed',
                                                        bottom: 0,
                                                        left: 0,
                                                        right: 0,
                                                        zIndex: theme.zIndex.appBar + 1,
                                                        backgroundColor: 'transparent',
                                                        padding: '6px 16px',
                                                        justifyContent: 'center',
                                                        '& .MuiBox-root': {
                                                            justifyContent: 'center',
                                                            flexDirection: 'row',
                                                            gap: '12px'
                                                        }
                                                    }
                                                    : {} }))] }), !isSmallScreen && (_jsx(Grid, { size: { xs: 12, lg: 4.5, xl: 4 }, children: _jsx(RightPanel, { watch: watch, backgroundImages: backgroundImages, onBackgroundImagesChange: setBackgroundImages, onPreviewLanguageChange: handlePreviewLanguageChange, selectedLanguageId: selectedLanguageId, entryMode: entryMode, hasStepParam: Boolean(stepParam), eventId: eventId }) }))] }) }) }), _jsx(Drawer, { anchor: 'bottom', open: Boolean(drawerOpen), onClose: () => setDrawerOpen(null), slotProps: {
                            paper: {
                                sx: {
                                    ...styles.bottomDrawer
                                }
                            }
                        }, children: drawerOpen === 'preview' && stepParam ? (_jsx(EventPreview, { watch: watch, backgroundImages: backgroundImages, selectedLanguageId: selectedLanguageId })) : (_jsx(StartScreenContent, { watch: watch, backgroundImages: backgroundImages, onBackgroundImagesChange: setBackgroundImages, entryMode: entryMode })) }), !isSmallScreen ? (_jsx(CustomDialog, { backgroundVariant: 'white', open: missingUndertakingDialogOpen, onClose: () => {
                            setMissingUndertakingDialogOpen(false);
                            // Clear validation errors when dialog closes
                            setUndertakingValidationErrors({});
                        }, title: t('undertaking.uploadUndertaking') || 'Upload Undertaking Files', closeIcon: true, maxWidth: 'md', actions: _jsxs(Box, { display: 'flex', gap: 1, width: '100%', children: [_jsx(Button, { size: 'large', fullWidth: true, variant: 'outlined', color: 'inherit', onClick: () => setMissingUndertakingDialogOpen(false), children: t('actions.close') }), _jsx(Button, { size: 'large', variant: 'contained', color: 'inherit', fullWidth: true, onClick: handleSaveMissingUndertakingFiles, children: t('actions.save') })] }), children: _jsxs(Box, { sx: { p: 3 }, children: [_jsx(Typography, { variant: 'body2', sx: { mb: 3 }, children: t('undertaking.uploadUndertakingLanguages') ||
                                        'Please upload undertaking files for the following languages:' }), _jsx(UndertakingField, { id: 'missing-undertaking-files', label: 'Undertaking', required: true, disabled: false, readonly: false, languages: languages?.filter((l) => missingUndertakingLanguageIds.includes(l.id)), filesByLanguage: missingLanguageFiles, languageErrors: undertakingValidationErrors, onLanguageFilesChange: (filesByLanguage) => {
                                        setMissingLanguageFiles(filesByLanguage);
                                        // Clear errors when files change
                                        setUndertakingValidationErrors({});
                                    } })] }) })) : (_jsx(BDrawer, { open: missingUndertakingDialogOpen, onClose: () => {
                            setMissingUndertakingDialogOpen(false);
                            // Clear validation errors when dialog closes
                            setUndertakingValidationErrors({});
                        }, title: t('undertaking.uploadUndertaking') || 'Upload Undertaking Files', showCloseButton: true, showTopBar: true, showBottomBar: true, dialogActions: _jsxs(Box, { display: 'flex', flexDirection: 'row', gap: 1, children: [_jsx(Button, { size: 'large', variant: 'contained', color: 'inherit', onClick: handleSaveMissingUndertakingFiles, fullWidth: true, children: t('actions.save') || 'Save ' }), _jsx(Button, { size: 'large', variant: 'outlined', color: 'inherit', onClick: () => setMissingUndertakingDialogOpen(false), fullWidth: true, children: t('actions.close') || 'Close' })] }), children: _jsxs(Box, { sx: { p: 2 }, children: [_jsx(Typography, { variant: 'body2', sx: { mb: 3 }, children: t('undertaking.uploadUndertakingLanguages') ||
                                        'Please upload undertaking files for the following languages:' }), _jsx(UndertakingField, { id: 'missing-undertaking-files-mobile', label: 'Undertaking', required: true, disabled: false, readonly: false, languages: languages?.filter((l) => missingUndertakingLanguageIds.includes(l.id)), filesByLanguage: missingLanguageFiles, languageErrors: undertakingValidationErrors, onLanguageFilesChange: (filesByLanguage) => {
                                        setMissingLanguageFiles(filesByLanguage);
                                        // Clear errors when files change
                                        setUndertakingValidationErrors({});
                                    } })] }) }))] }), showDiscardChangesPopup && (_jsx(CustomDialog, { open: showDiscardChangesPopup, backgroundVariant: 'white', title: t('events.discardPopup.title'), onClose: () => setShowDiscardChangesPopup(false), actions: _jsxs(Box, { display: 'flex', gap: 1, width: '100%', children: [_jsx(Button, { size: 'large', variant: 'outlined', color: 'inherit', onClick: () => setShowDiscardChangesPopup(false), fullWidth: true, children: t('actions.cancel') || 'Cancel' }), _jsx(Button, { size: 'large', variant: 'contained', color: 'inherit', onClick: () => {
                                setShowDiscardChangesPopup(false);
                                navigate(`/event/preview/${eventId}`);
                            }, fullWidth: true, children: t('actions.goToPreview') || 'Discard' })] }), children: _jsx(Typography, { variant: 'body1', color: 'textPrimary', sx: { px: 3 }, children: t('events.discardPopup.description') }) }))] }));
};
export default CreateEvent;
